import React, { useState, useMemo, useEffect, useCallback } from 'react'
import { formatDeliveryDays } from 'src/utils/deliveryDays'
import IronWebCatalogueStatus from 'src/screens/suppliers/IronWebCatalogueStatus'
import { useDispatch, useSelector } from 'react-redux'
import {
  Card,
  Select,
  Empty,
  Table,
  Tag,
  Button,
  Popconfirm,
  Input,
  Checkbox,
  Row,
  Col,
  Modal,
  Alert,
  Collapse,
  message,
  Tooltip,
  Spin,
  Upload,
  Form,
} from 'antd'
import {
  FileTextOutlined,
  SearchOutlined,
  InfoCircleOutlined,
  CalendarOutlined,
  ShopOutlined,
  DownloadOutlined,
  UserOutlined,
  CloseOutlined,
  QuestionCircleOutlined,
  MailOutlined,
  EditOutlined,
  PlusOutlined,
  InboxOutlined,
} from '@ant-design/icons'
import styled from 'styled-components/macro'
import FadeIn from 'src/components/styled/FadeIn'
import Title from 'src/branding/Title'
import moment from 'moment'
import {
  fetchPriceFiles,
  fetchPriceFileMetadata,
  editSupplier,
} from 'src/features/SupplierSlice'
import { setAccount } from 'src/features/AuthSlice'
import {
  adminFetchProducts,
  adminSearchProducts,
  adminEditProduct,
  adminDeleteProduct,
} from 'src/features/ProductSlice'
import EditProductModal from 'src/screens/products/EditProductModal'
import { uploadFile } from 'src/utils/uploadFile'
import { API } from 'src/utils/interceptor'
import { Auth } from 'aws-amplify'
import { CSVLink } from 'react-csv'
import axios from 'axios'
import { arrayToCSV } from 'src/screens/products/Utils'
import Sentry from 'src/utils/sentry'
import DeliveryDaysSelect from 'src/components/DeliveryDaysField'
import { generateFormatHash } from 'src/utils/formatHash'
import {
  BulkPriceFileEditModal,
  ScheduledUploadsSection,
  isValidationErrorMessage,
  parseValidationErrors,
} from 'src/screens/products/bulk-edit'
import ProductPriceAuditModal from 'src/screens/products/ProductPriceAuditModal'
import { useProductPriceAudit } from 'src/screens/products/useProductPriceAudit'
import { canViewProductPriceAudit } from 'src/utils/canViewProductPriceAudit'

const { Option } = Select
const { Search } = Input
const { Panel } = Collapse

// mock data removed; using real API data

const SupplierProductFiles = () => {
  const getErrorMessage = error => {
    // Check for validation errors from the API
    const apiMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.response?.data?.errorMessage

    if (apiMessage) {
      // If it's a validation error, show it more prominently
      if (
        apiMessage.includes('Validation failed') ||
        apiMessage.includes('Row')
      ) {
        return apiMessage
      }
      // Check for syntax errors or server errors
      if (
        apiMessage.includes('SyntaxError') ||
        apiMessage.includes('Unexpected token')
      ) {
        return 'Server error: There was a problem processing your file. Please contact OMS support and report this error.'
      }
      return apiMessage
    }

    // Check for network/connection errors
    if (
      error?.code === 'NETWORK_ERROR' ||
      error?.message?.includes('Network Error')
    ) {
      return 'Network error: Could not connect to the server. Please check your connection and try again.'
    }

    // Check for 500 errors
    if (error?.response?.status === 500) {
      const serverError =
        error?.response?.data?.message || error?.response?.data?.error
      if (serverError) {
        return `Server error: ${serverError}. Please contact OMS support if this persists.`
      }
      return 'Server error: Something went wrong on our end. Please contact OMS support and report this error.'
    }

    // Check for 400 errors (bad request)
    if (error?.response?.status === 400) {
      return (
        apiMessage || 'Invalid request. Please check your file and try again.'
      )
    }

    // Generic error message
    return (
      error?.message ||
      'Something went wrong. Please try again or contact OMS support if the problem persists.'
    )
  }
  const dispatch = useDispatch()

  const currentUser = useSelector(state => state.auth.account)
  const priceFiles = currentUser?.supplier || []
  const priceFileMetadata = useSelector(
    state => state.suppliers.priceFileMetadata,
  )
  const isPriceFilesLoading = useSelector(
    state => state.suppliers.isPriceFilesLoading,
  )
  const isMetadataLoading = useSelector(
    state => state.suppliers.isMetadataLoading,
  )
  const products = useSelector(state => state.product.products)
  const totalProducts = useSelector(
    state => state.product.productStats?.serverCount || 0,
  )
  const [productsLoading, setProductsLoading] = useState(false)

  const [selectedPriceFileId, setSelectedPriceFileId] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [searchFields, setSearchFields] = useState(['title', 'sku'])
  const [paginationPage, setPaginationPage] = useState(1)
  const [paginationSize, setPaginationSize] = useState(250)
  const [expandedVenues, setExpandedVenues] = useState(false)
  const [venueAssignmentModalVisible, setVenueAssignmentModalVisible] =
    useState(false)
  const [selectedVenuesForAssignment, setSelectedVenuesForAssignment] =
    useState([])
  const [isVenueAssignmentSaving, setIsVenueAssignmentSaving] = useState(false)
  const [venueSearchTerm, setVenueSearchTerm] = useState('')
  const [venueFilterType, setVenueFilterType] = useState('all') // 'all', 'onThisFile', 'onOtherFiles'
  const [isAdvertDismissed, setIsAdvertDismissed] = useState(false)
  const [educationalContentExpanded, setEducationalContentExpanded] =
    useState(false)
  const [isEditModalVisible, setIsEditModalVisible] = useState(false)
  const [editedProduct, setEditedProduct] = useState(null)
  const [isEditSaving, setIsEditSaving] = useState(false)
  const priceAudit = useProductPriceAudit()
  const showPriceAudit = canViewProductPriceAudit(currentUser)
  const [isBulkModalVisible, setIsBulkModalVisible] = useState(false)
  const [bulkFile, setBulkFile] = useState(null)
  const [bulkFileHeaders, setBulkFileHeaders] = useState([])
  const [bulkFieldMapping, setBulkFieldMapping] = useState({})
  const [validationErrors, setValidationErrors] = useState([])
  const [isValidationErrorModalVisible, setIsValidationErrorModalVisible] =
    useState(false)
  const [isEditFileNameModalVisible, setIsEditFileNameModalVisible] =
    useState(false)
  const [editingFileName, setEditingFileName] = useState('')
  const [isSavingFileName, setIsSavingFileName] = useState(false)
  const [fileNameError, setFileNameError] = useState('')
  const [deletingProductId, setDeletingProductId] = useState(null)
  const [bulkUploadBucket, setBulkUploadBucket] = useState(null)
  const [bulkUploadKey, setBulkUploadKey] = useState(null)
  const [isMappingScanning, setIsMappingScanning] = useState(false)
  const [removeMissingProducts, setRemoveMissingProducts] = useState(false)
  const [projectedRemovals, setProjectedRemovals] = useState(null)
  const [projectedRemovalSkus, setProjectedRemovalSkus] = useState([])
  const [isDeletionListVisible, setIsDeletionListVisible] = useState(false)
  const [isFetchingDeletionSkus, setIsFetchingDeletionSkus] = useState(false)
  const [projectedNewProducts, setProjectedNewProducts] = useState(null)
  const [projectedNewProductSkus, setProjectedNewProductSkus] = useState([])
  const [isNewProductListVisible, setIsNewProductListVisible] = useState(false)
  const [isFetchingNewProductSkus, setIsFetchingNewProductSkus] =
    useState(false)
  const [csvProducts, setCsvProducts] = useState([])
  const [csvLoading, setCsvLoading] = useState(false)
  const csvButtonRef = React.useRef(null)
  const [isBulkApplying, setIsBulkApplying] = useState(false)
  const [bulkProgressMessage, setBulkProgressMessage] = useState('')
  const [bulkApplySummary, setBulkApplySummary] = useState(null)
  const [isGuideExpanded, setIsGuideExpanded] = useState(false)
  const [isCreatePriceFileModalVisible, setIsCreatePriceFileModalVisible] =
    useState(false)
  const [createStep, setCreateStep] = useState(1)
  // eslint-disable-next-line no-unused-vars
  const [createdPriceFileId, setCreatedPriceFileId] = useState(null)
  const [createPriceFileForm, setCreatePriceFileForm] = useState({
    secondTitle: '',
    orderEmail: '',
    contactNumber: '',
    minOrderValue: '',
    deliveryDays: [],
    cutOffTime: '',
  })
  const [createPriceFileErrors, setCreatePriceFileErrors] = useState({})
  const [createBulkFile, setCreateBulkFile] = useState(null)
  const [createBulkFileHeaders, setCreateBulkFileHeaders] = useState([])
  const [createBulkFieldMapping, setCreateBulkFieldMapping] = useState({})
  const [createBulkUploadBucket, setCreateBulkUploadBucket] = useState(null)
  const [createBulkUploadKey, setCreateBulkUploadKey] = useState(null)
  const [isCreateMappingScanning, setIsCreateMappingScanning] = useState(false)
  const [isCreateBulkApplying, setIsCreateBulkApplying] = useState(false)
  const [isEditSettingsModalVisible, setIsEditSettingsModalVisible] =
    useState(false)
  const [editSettingsForm, setEditSettingsForm] = useState({
    secondTitle: '',
    orderEmail: '',
    contactNumber: '',
    minOrderValue: '',
    deliveryDays: [],
    cutOffTime: '',
  })
  const [editSettingsErrors, setEditSettingsErrors] = useState({})
  // Scheduled uploads state
  const [scheduledUploads, setScheduledUploads] = useState([])
  const [isLoadingScheduledUploads, setIsLoadingScheduledUploads] =
    useState(false)
  const [hasFetchedScheduledUploads, setHasFetchedScheduledUploads] =
    useState(false)
  const [isScheduleEnabled, setIsScheduleEnabled] = useState(false)
  const [scheduledDate, setScheduledDate] = useState(null)
  const [scheduledTime, setScheduledTime] = useState(null)
  const [scheduleError, setScheduleError] = useState('')
  const [isScheduleConflictModalVisible, setIsScheduleConflictModalVisible] =
    useState(false)
  const [conflictingSchedule, setConflictingSchedule] = useState(null)
  const [isScheduledUploadsCollapsed, setIsScheduledUploadsCollapsed] =
    useState(true) // Start collapsed - user opens to trigger fetch
  const [hasUserTouchedMapping, setHasUserTouchedMapping] = useState(false)
  const [autoFilledFields, setAutoFilledFields] = useState(new Set())
  const [mappingLastUsedAt, setMappingLastUsedAt] = useState(null)
  const bulkMappingFields = useMemo(
    () => [
      {
        key: 'title',
        label: 'Product Title',
        required: true,
        helper:
          'What your buyers see. Must not be empty. Maximum 500 characters. Examples: "Ribeye Steak", "Chicken Breast 500g"',
      },
      {
        key: 'sku',
        label: 'SKU / Product Code',
        required: true,
        helper:
          'Used to match existing products (case-insensitive). Must not be empty. Maximum 100 characters. Examples: "RIB01", "CHK-BREAST-500". Note: "abc" will match "ABC"',
      },
      {
        key: 'size',
        label: 'Size',
        required: true,
        helper:
          'Pack size description. Must not be empty. Examples: "1x1kg", "10 x 200g", "1 x 2.5kg", "24x100gr"',
      },
      {
        key: 'price',
        label: 'Customer Price',
        required: true,
        helper:
          'Must be a number only (no currency symbols). Cannot be negative or text. Examples: 25.50, 100, 12.99. Invalid: "£25", "Tbc", "N/A"',
      },
      {
        key: 'type',
        label: 'Type',
        required: true,
        helper:
          'Product category. Must not be empty. Examples: "Meat & Poultry", "Beef", "Steaks", "Game", "Sausages & Breakfast"',
      },
      {
        key: 'hasVat',
        label: 'Has VAT',
        required: true,
        helper:
          'Must be: Yes/No, True/False, or 1/0 (case-insensitive). Examples: "Yes", "TRUE", "1", "No", "FALSE", "0"',
      },
      {
        key: 'UoM',
        label: 'Unit of Measure',
        required: false,
        helper:
          'Optional: Unit of measurement. Common values: "Kg", "Each", "Pack", "Box", "g", "L", "ml". Leave blank if not applicable.',
      },
      {
        key: 'pricedBy',
        label: 'Priced By',
        required: false,
        helper:
          'Optional: Overrides UoM if you price differently. Examples: "Weight", "Volume", "Count". Leave blank if not needed.',
      },
      {
        key: 'GLN',
        label: 'GLN',
        required: false,
        helper:
          'Optional: Global Location Number. Must be numeric if provided (max 20 digits). Examples: "1234567890123", "0". Leave blank if not applicable.',
      },
      {
        key: 'cloudinaryImageId',
        label: 'Image URL / ID',
        required: false,
        helper:
          'Optional: Must be a valid HTTP/HTTPS URL or Cloudinary ID if provided. Examples: "https://example.com/image.jpg" or "products/meat/steak". Leave blank if no image.',
      },
    ],
    [],
  )

  const isMappingIncomplete = useMemo(
    () =>
      bulkMappingFields
        .filter(field => field.required)
        .some(field => !bulkFieldMapping[field.key]),
    [bulkFieldMapping, bulkMappingFields],
  )

  const createIsMappingIncomplete = useMemo(
    () =>
      bulkMappingFields
        .filter(field => field.required)
        .some(field => !createBulkFieldMapping[field.key]),
    [bulkMappingFields, createBulkFieldMapping],
  )

  const showValidationErrors = useCallback(errorMsg => {
    const errors = parseValidationErrors(errorMsg)
    if (errors.length === 0) return false
    setValidationErrors(errors)
    setIsValidationErrorModalVisible(true)
    return true
  }, [])

  const getBaseSupplierInfo = () => {
    if (!priceFiles || priceFiles.length === 0) {
      return {
        title: 'Your Supplier',
        logoUrl: '',
        integrationType: '',
      }
    }
    const first = priceFiles[0]
    return {
      title: first.title || 'Your Supplier',
      logoUrl: first.logoUrl || '',
      integrationType: first.integrationType || '',
    }
  }

  const resetCreatePriceFileState = () => {
    setCreateStep(1)
    setCreatePriceFileForm({
      secondTitle: '',
      orderEmail: '',
      contactNumber: '',
      minOrderValue: '',
      deliveryDays: [],
      cutOffTime: '',
    })
    setCreatePriceFileErrors({})
    setCreatedPriceFileId(null)
    setCreateBulkFile(null)
    setCreateBulkFileHeaders([])
    setCreateBulkFieldMapping({})
    setCreateBulkUploadKey(null)
    setCreateBulkUploadBucket(null)
    setIsCreateMappingScanning(false)
    setIsCreateBulkApplying(false)
  }

  const openEditSettingsModal = () => {
    const selectedPriceFile = priceFiles.find(
      pf => pf._id === selectedPriceFileId,
    )
    if (!selectedPriceFile) return

    const metadataForFile = priceFileMetadata[selectedPriceFileId] || {}

    setEditSettingsForm({
      secondTitle: selectedPriceFile.secondTitle || '',
      orderEmail: metadataForFile.emailAddress || '',
      contactNumber: metadataForFile.phoneNumber || '',
      minOrderValue:
        typeof metadataForFile.minOrderValue === 'number'
          ? String(metadataForFile.minOrderValue / 100)
          : '',
      deliveryDays: metadataForFile.deliveryDays || [],
      cutOffTime: metadataForFile.orderCutOffTime || '',
    })
    setEditSettingsErrors({})
    setIsEditSettingsModalVisible(true)
  }

  const resetEditSettingsState = () => {
    setIsEditSettingsModalVisible(false)
    setEditSettingsForm({
      secondTitle: '',
      orderEmail: '',
      contactNumber: '',
      minOrderValue: '',
      deliveryDays: [],
      cutOffTime: '',
    })
    setEditSettingsErrors({})
  }

  const validateEditSettings = () => {
    const errors = {}
    const trimmedSecondTitle = editSettingsForm.secondTitle.trim()
    const trimmedOrderEmail = editSettingsForm.orderEmail.trim()
    const trimmedContactNumber = editSettingsForm.contactNumber.trim()
    const trimmedMinOrder =
      editSettingsForm.minOrderValue === null
        ? ''
        : String(editSettingsForm.minOrderValue).trim()

    if (!trimmedSecondTitle) {
      errors.secondTitle = 'Please enter a price file name'
    }
    if (!trimmedOrderEmail) {
      errors.orderEmail = 'Please enter an order email address'
    } else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmedOrderEmail)) {
      errors.orderEmail = 'Please enter a valid email address'
    }
    if (!trimmedContactNumber) {
      errors.contactNumber = 'Please enter a contact number'
    }
    if (trimmedMinOrder && Number.isNaN(Number.parseFloat(trimmedMinOrder))) {
      errors.minOrderValue = 'Minimum order must be a number or left blank'
    }
    if (
      !Array.isArray(editSettingsForm.deliveryDays) ||
      editSettingsForm.deliveryDays.length === 0
    ) {
      errors.deliveryDays = 'Please select at least one delivery day'
    }

    setEditSettingsErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSaveEditSettings = async () => {
    if (!selectedPriceFileId) return
    if (!validateEditSettings()) return

    const selectedPriceFile = priceFiles.find(
      pf => pf._id === selectedPriceFileId,
    )
    if (!selectedPriceFile) return

    const trimmedMinOrder =
      editSettingsForm.minOrderValue?.toString().trim() || ''
    const minOrderValue =
      trimmedMinOrder && !Number.isNaN(Number.parseFloat(trimmedMinOrder))
        ? Math.round(Number.parseFloat(trimmedMinOrder) * 100)
        : 0

    const updatedSupplier = {
      ...selectedPriceFile,
      secondTitle: editSettingsForm.secondTitle.trim(),
      emailAddress: editSettingsForm.orderEmail.trim(),
      phoneNumber: editSettingsForm.contactNumber.trim(),
      deliveryDays: editSettingsForm.deliveryDays,
      minOrderValue,
      orderCutOffTime: editSettingsForm.cutOffTime || '',
    }

    try {
      const saved = await dispatch(editSupplier(updatedSupplier))
      if (saved === false) {
        message.error('Failed to update file settings. Please try again.')
        return
      }
      await dispatch(fetchPriceFiles())
      dispatch(fetchPriceFileMetadata(selectedPriceFileId))
      if (currentUser?.supplier?.length && saved?._id) {
        dispatch(
          setAccount({
            ...currentUser,
            supplier: currentUser.supplier.map(pf =>
              pf._id === saved._id ? { ...pf, ...saved } : pf,
            ),
          }),
        )
      }
      message.success('File settings updated.')
      resetEditSettingsState()
    } catch (error) {
      console.error('Failed to update file settings:', error)
      message.error('Failed to update file settings. Please try again.')
    }
  }

  const validateCreateStep1 = () => {
    const errors = {}
    const trimmedSecondTitle = createPriceFileForm.secondTitle.trim()
    const trimmedOrderEmail = createPriceFileForm.orderEmail.trim()
    const trimmedContactNumber = createPriceFileForm.contactNumber.trim()
    const trimmedMinOrderValue =
      createPriceFileForm.minOrderValue === null
        ? ''
        : String(createPriceFileForm.minOrderValue).trim()

    if (!trimmedSecondTitle) {
      errors.secondTitle = 'Please enter a price file name'
    }
    if (!trimmedOrderEmail) {
      errors.orderEmail = 'Please enter an order email address'
    } else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmedOrderEmail)) {
      errors.orderEmail = 'Please enter a valid email address'
    }
    if (!trimmedContactNumber) {
      errors.contactNumber = 'Please enter a contact number'
    }
    if (
      trimmedMinOrderValue &&
      Number.isNaN(Number.parseFloat(trimmedMinOrderValue))
    ) {
      errors.minOrderValue = 'Minimum order must be a number or left blank'
    }
    if (
      !Array.isArray(createPriceFileForm.deliveryDays) ||
      createPriceFileForm.deliveryDays.length === 0
    ) {
      errors.deliveryDays = 'Please select at least one delivery day'
    }

    setCreatePriceFileErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleCreateStep1Next = async () => {
    if (!validateCreateStep1()) return

    try {
      const session = await Auth.currentAuthenticatedUser()
      const username = session?.attributes?.sub

      if (!username) {
        message.error(
          'We could not identify your user account. Please sign out and back in.',
        )
        return
      }

      const trimmedMinOrder =
        createPriceFileForm.minOrderValue?.toString().trim() || ''
      const minOrderValue =
        trimmedMinOrder && !Number.isNaN(Number.parseFloat(trimmedMinOrder))
          ? Number.parseFloat(trimmedMinOrder)
          : undefined

      const payload = {
        username,
        secondTitle: createPriceFileForm.secondTitle.trim(),
        emailAddress: createPriceFileForm.orderEmail.trim(),
        phoneNumber: createPriceFileForm.contactNumber.trim(),
        deliveryDays: createPriceFileForm.deliveryDays,
        orderCutOffTime: createPriceFileForm.cutOffTime || '',
        minOrderValue,
      }

      const created = await API.post('api', 'supplier/price-files', {
        body: payload,
      })

      if (!created?._id) {
        message.warning(
          'The price file was created, but we could not read its ID. You may need to refresh the page.',
        )
      } else {
        setCreatedPriceFileId(created._id)
      }
    } catch (error) {
      console.error('Failed to create price file', error)
      message.error(getErrorMessage(error))
      return
    }

    setCreateBulkFieldMapping(
      bulkMappingFields.reduce((acc, field) => {
        acc[field.key] = ''
        return acc
      }, {}),
    )

    setCreateStep(2)
  }

  const createUploadProps = {
    name: 'file',
    multiple: false,
    customRequest: ({ onSuccess }) => {
      onSuccess('ok')
    },
    beforeUpload: async file => {
      try {
        if (!createdPriceFileId) {
          message.warning(
            'There was a problem finding your new price file. Please close this window and try again.',
          )
          return Upload.LIST_IGNORE
        }

        setCreateBulkFile(file)
        setIsCreateMappingScanning(true)
        setCreateBulkUploadKey(null)
        setCreateBulkUploadBucket(null)
        setCreateBulkFileHeaders([])
        setCreateBulkFieldMapping(
          bulkMappingFields.reduce((acc, field) => {
            acc[field.key] = ''
            return acc
          }, {}),
        )

        const uploadResult = await uploadFile(file)
        if (!uploadResult?.keyName) {
          throw new Error('Upload failed. Please try again.')
        }

        setCreateBulkUploadKey(uploadResult.keyName)
        setCreateBulkUploadBucket(
          uploadResult.bucketName || 'invoices-ptotos-staging',
        )

        const session = await Auth.currentAuthenticatedUser()
        const response = await API.post(
          'api',
          `supplier/${createdPriceFileId}/price-file-upload`,
          {
            body: {
              fileKey: uploadResult.keyName,
              bucketName: uploadResult.bucketName || 'invoices-ptotos-staging',
              username: session?.attributes?.sub,
            },
          },
        )

        setCreateBulkFileHeaders(response.headers || [])
        setIsCreateMappingScanning(false)
        message.success('File uploaded. Please map your columns.')
      } catch (error) {
        console.error(error)
        message.error(getErrorMessage(error))
        setCreateBulkFile(null)
        setCreateBulkUploadKey(null)
        setCreateBulkUploadBucket(null)
        setCreateBulkFileHeaders([])
        setIsCreateMappingScanning(false)
      }
      return false
    },
    onRemove: () => {
      setCreateBulkFile(null)
      setCreateBulkUploadKey(null)
      setCreateBulkUploadBucket(null)
      setCreateBulkFileHeaders([])
      setCreateBulkFieldMapping(
        bulkMappingFields.reduce((acc, field) => {
          acc[field.key] = ''
          return acc
        }, {}),
      )
    },
    fileList: createBulkFile ? [createBulkFile] : [],
    showUploadList: {
      showRemoveIcon: !isCreateBulkApplying,
      showPreviewIcon: false,
    },
  }

  const handleCreateMappingChange = (fieldKey, value) => {
    setCreateBulkFieldMapping(prev => ({
      ...prev,
      [fieldKey]: value,
    }))
  }

  const handleCreateApplyChanges = async () => {
    if (!createBulkFile || !createdPriceFileId) {
      message.error('Please upload a valid file before applying changes.')
      return
    }
    if (!createBulkUploadKey || !createBulkUploadBucket) {
      message.error(
        'There was a problem with the uploaded file. Please try again.',
      )
      return
    }
    if (createIsMappingIncomplete) {
      message.warning('Please map all required fields before continuing.')
      return
    }

    try {
      setIsCreateBulkApplying(true)
      const session = await Auth.currentAuthenticatedUser()
      await API.post('api', `supplier/${createdPriceFileId}/price-file-apply`, {
        body: {
          fileKey: createBulkUploadKey,
          bucketName: createBulkUploadBucket,
          mapping: createBulkFieldMapping,
          headers: createBulkFileHeaders, // Pass headers for saving mapping
          removeMissing: false,
          username: session?.attributes?.sub,
        },
      })

      message.success('New price file created and products imported.')

      try {
        await API.get('api', `admin/search/indexProducts/${createdPriceFileId}`)
        message.success('Refreshing catalogue search results…')
      } catch (reindexError) {
        console.error(
          'Failed to reindex search after creating price file:',
          reindexError,
        )
        Sentry.captureException(reindexError)
        message.warning(
          'Price file updated, but the catalogue search index could not be refreshed automatically. Please trigger a manual reindex.',
        )
      }

      // Refresh data and auto-select the new price file
      // await dispatch(fetchPriceFiles())
      setSelectedPriceFileId(createdPriceFileId)
      setPaginationPage(1)
      dispatch(fetchPriceFileMetadata(createdPriceFileId))
      await dispatch(
        adminFetchProducts({
          page: 1,
          limit: paginationSize,
          supplierId: createdPriceFileId,
          onSuccess: () => setProductsLoading(false),
          onFailure: () => setProductsLoading(false),
        }),
      )

      setIsCreateBulkApplying(false)
      setIsCreatePriceFileModalVisible(false)
      resetCreatePriceFileState()
    } catch (error) {
      console.error('Create price file apply error:', error)
      console.error('Error response:', error?.response)
      const errorMsg = getErrorMessage(error)
      if (isValidationErrorMessage(errorMsg)) {
        showValidationErrors(errorMsg)
      } else {
        message.error({
          content: errorMsg,
          duration: 8,
          style: { marginTop: '20vh' },
        })
      }
      setIsCreateBulkApplying(false)
    }
  }

  useEffect(() => {
    setBulkFieldMapping(
      bulkMappingFields.reduce((acc, field) => {
        acc[field.key] = ''
        return acc
      }, {}),
    )
  }, [bulkMappingFields])

  const runDeletionPreview = async includeList => {
    if (
      !removeMissingProducts ||
      !bulkUploadKey ||
      !bulkUploadBucket ||
      isMappingIncomplete ||
      !selectedPriceFileId
    ) {
      return
    }
    if (includeList) setIsFetchingDeletionSkus(true)
    try {
      const session = await Auth.currentAuthenticatedUser()
      const response = await API.post(
        'api',
        `supplier/${selectedPriceFileId}/price-file-apply`,
        {
          body: {
            fileKey: bulkUploadKey,
            bucketName: bulkUploadBucket,
            mapping: bulkFieldMapping,
            removeMissing: true,
            dryRun: true,
            includeDeletions: includeList,
            username: session?.attributes?.sub,
          },
        },
      )
      setProjectedRemovals(response.deleted)
      if (includeList) {
        setProjectedRemovalSkus(response.deletions || [])
      } else {
        setIsDeletionListVisible(false)
        setProjectedRemovalSkus([])
      }
    } catch (error) {
      console.error('Deletion preview error:', error)
      console.error('Error response:', error?.response)
      const errorMsg = getErrorMessage(error)
      if (isValidationErrorMessage(errorMsg)) {
        showValidationErrors(errorMsg)
      } else {
        message.error(errorMsg)
      }
      setProjectedRemovals(null)
      setProjectedRemovalSkus([])
      setIsDeletionListVisible(false)
    } finally {
      if (includeList) setIsFetchingDeletionSkus(false)
    }
  }

  const runNewProductPreview = async includeList => {
    if (
      !bulkUploadKey ||
      !bulkUploadBucket ||
      isMappingIncomplete ||
      !selectedPriceFileId
    ) {
      return
    }
    if (includeList) setIsFetchingNewProductSkus(true)
    try {
      const session = await Auth.currentAuthenticatedUser()
      const response = await API.post(
        'api',
        `supplier/${selectedPriceFileId}/price-file-apply`,
        {
          body: {
            fileKey: bulkUploadKey,
            bucketName: bulkUploadBucket,
            mapping: bulkFieldMapping,
            removeMissing: false,
            dryRun: true,
            includeNewSkus: includeList,
            username: session?.attributes?.sub,
          },
        },
      )
      setProjectedNewProducts(response.newSkusCount || 0)
      if (includeList) {
        setProjectedNewProductSkus(response.newSkus || [])
      } else {
        setIsNewProductListVisible(false)
        setProjectedNewProductSkus([])
      }
    } catch (error) {
      console.error('New product preview error:', error)
      console.error('Error response:', error?.response)
      const errorMsg = getErrorMessage(error)
      if (isValidationErrorMessage(errorMsg)) {
        showValidationErrors(errorMsg)
      } else {
        message.error(errorMsg)
      }
      setProjectedNewProducts(null)
      setProjectedNewProductSkus([])
      setIsNewProductListVisible(false)
    } finally {
      if (includeList) setIsFetchingNewProductSkus(false)
    }
  }

  useEffect(() => {
    if (
      removeMissingProducts &&
      !isMappingScanning &&
      validationErrors.length === 0
    ) {
      runDeletionPreview(false)
    } else {
      setProjectedRemovals(null)
      setProjectedRemovalSkus([])
      setIsDeletionListVisible(false)
    }
  }, [
    removeMissingProducts,
    bulkUploadKey,
    bulkUploadBucket,
    bulkFieldMapping,
    isMappingIncomplete,
    isMappingScanning,
    validationErrors.length,
  ])

  useEffect(() => {
    // Don't run preview if there are validation errors
    if (
      !isMappingScanning &&
      bulkUploadKey &&
      bulkUploadBucket &&
      !isMappingIncomplete &&
      validationErrors.length === 0
    ) {
      runNewProductPreview(false)
    } else {
      setProjectedNewProducts(null)
      setProjectedNewProductSkus([])
      setIsNewProductListVisible(false)
    }
  }, [
    bulkUploadKey,
    bulkUploadBucket,
    bulkFieldMapping,
    isMappingIncomplete,
    isMappingScanning,
    validationErrors.length,
  ])

  // Auto-fill mapping when headers are extracted and no user interaction yet
  useEffect(() => {
    const autoFillMapping = async () => {
      if (
        !bulkFileHeaders.length ||
        isMappingScanning ||
        hasUserTouchedMapping ||
        !selectedPriceFileId
      ) {
        return
      }

      try {
        // Get username for authorization
        const session = await Auth.currentAuthenticatedUser()
        const username = session?.attributes?.sub

        if (!username) {
          return
        }

        // Generate hash
        const formatHash = generateFormatHash(bulkFileHeaders)

        const response = await API.get(
          'api',
          `supplier/${selectedPriceFileId}/upload-mappings`,
          {
            queryStringParameters: { formatHash, username },
          },
        )

        if (response?.data?.mapping) {
          setBulkFieldMapping(response.data.mapping)
          setMappingLastUsedAt(response.data.lastUsedAt)

          // Track which fields were auto-filled
          const filledFields = new Set()
          Object.keys(response.data.mapping).forEach(key => {
            if (response.data.mapping[key]) {
              filledFields.add(key)
            }
          })
          setAutoFilledFields(filledFields)

          message.success(
            'We recognised this file format — your mappings have been auto-filled.',
            3,
          )
        }
      } catch (error) {
        // Silent failure - don't show error to user
        console.error('Auto-fill failed:', error)
      }
    }

    autoFillMapping()
  }, [
    bulkFileHeaders,
    isMappingScanning,
    hasUserTouchedMapping,
    selectedPriceFileId,
  ])

  // Fetch price files on mount
  // useEffect(() => {
  //   dispatch(fetchPriceFiles())
  // }, [dispatch])

  // // Fetch metadata for all price files when they're loaded (so venue assignment modal has all data)
  // useEffect(() => {
  //   if (priceFiles && priceFiles.length > 0) {
  //     // Check if there are any price files missing metadata
  //     const missingMetadata = priceFiles.filter(
  //       priceFile => !priceFileMetadata[priceFile._id],
  //     )

  //     // Only fetch if there are price files missing metadata
  //     if (missingMetadata.length > 0) {
  //       missingMetadata.forEach(priceFile => {
  //         dispatch(fetchPriceFileMetadata(priceFile._id))
  //       })
  //     }
  //   }
  // }, [dispatch, priceFiles, priceFileMetadata])

  // Fetch metadata when price file is selected (for immediate display)
  useEffect(() => {
    if (selectedPriceFileId) {
      dispatch(fetchPriceFileMetadata(selectedPriceFileId))
    }
  }, [dispatch, selectedPriceFileId])

  // Debounce the search term so we only hit the search API once typing pauses
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 400)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // Fetch products for the selected price file from the server for the
  // current page. Search is also performed server-side so it covers the
  // entire price file, not just the products loaded on the current page.
  const fetchProductsForCurrentView = useCallback(
    ({
      page = paginationPage,
      limit = paginationSize,
      searchTerm: term = debouncedSearchTerm,
    } = {}) => {
      setProductsLoading(true)
      const onDone = () => setProductsLoading(false)
      if (term.trim()) {
        dispatch(
          adminSearchProducts({
            searchTerm: term.trim(),
            searchFields,
            supplierId: selectedPriceFileId,
            page,
            limit,
            onSuccess: onDone,
            onFailure: onDone,
          }),
        )
      } else {
        dispatch(
          adminFetchProducts({
            page,
            limit,
            supplierId: selectedPriceFileId,
            onSuccess: onDone,
            onFailure: onDone,
          }),
        )
      }
    },
    [
      dispatch,
      paginationPage,
      paginationSize,
      debouncedSearchTerm,
      searchFields,
      selectedPriceFileId,
    ],
  )

  useEffect(() => {
    if (!selectedPriceFileId) return
    fetchProductsForCurrentView()
  }, [selectedPriceFileId, fetchProductsForCurrentView])

  const handlePriceFileChange = value => {
    setSelectedPriceFileId(value)
    setSearchTerm('')
    setPaginationPage(1)
    // Reset venue assignment selection when changing price file
    if (value && priceFileMetadata[value]) {
      setSelectedVenuesForAssignment(
        priceFileMetadata[value].venues?.map(v => v._id) || [],
      )
    } else {
      setSelectedVenuesForAssignment([])
    }
  }

  const handleDownloadCSV = async () => {
    if (!selectedPriceFileId) {
      message.warning('Please select a price file first')
      return
    }

    setCsvLoading(true)
    try {
      const { filePath } = await API.get(
        'api',
        `supplier/${selectedPriceFileId}/products`,
      )

      if (filePath) {
        const {
          data: { products },
        } = await axios(filePath)
        const formattedProducts = arrayToCSV(products)
        setCsvProducts(formattedProducts)

        // Trigger download
        if (csvButtonRef.current?.link) {
          await csvButtonRef.current.link.click()
          setCsvProducts([])
          message.success('CSV file downloaded successfully')
        } else {
          throw new Error('Download link not available')
        }
      } else {
        throw new Error('No file path returned from server')
      }
    } catch (error) {
      console.error('Download error:', error)
      Sentry.captureException(error)
      message.error(
        getErrorMessage(error) ||
          'Failed to download CSV file. Please try again.',
      )
      setCsvProducts([])
    } finally {
      setCsvLoading(false)
    }
  }

  const handleVenueAssignmentOpen = async () => {
    // Reset search and filter
    setVenueSearchTerm('')
    setVenueFilterType('all')

    // Ensure metadata for all price files is fetched (safety net in case useEffect didn't catch them all)
    if (priceFiles && priceFiles.length > 0) {
      const fetchPromises = priceFiles.map(priceFile => {
        // Only fetch if we don't already have metadata for this price file
        if (!priceFileMetadata[priceFile._id]) {
          return dispatch(fetchPriceFileMetadata(priceFile._id))
        }
        return Promise.resolve()
      })
      await Promise.all(fetchPromises)
    }

    // Set initial selection to venues already on current price file
    if (selectedPriceFileId && priceFileMetadata[selectedPriceFileId]) {
      setSelectedVenuesForAssignment(
        priceFileMetadata[selectedPriceFileId].venues?.map(v => v._id) || [],
      )
    } else {
      setSelectedVenuesForAssignment([])
    }
    setVenueAssignmentModalVisible(true)
  }

  const handleVenueAssignmentSave = async () => {
    if (!selectedPriceFileId) {
      message.warning('Please select a price file first.')
      return
    }

    const moveCount = venuesToMove.length
    if (moveCount === 0) {
      message.info('No changes to save')
      return
    }

    setIsVenueAssignmentSaving(true)

    try {
      const session = await Auth.currentAuthenticatedUser()
      const username = session?.attributes?.sub

      const response = await API.post(
        'api',
        `supplier/${selectedPriceFileId}/assign-venues`,
        {
          body: {
            venueIds: selectedVenuesForAssignment,
            username,
          },
        },
      )

      if (response.success) {
        message.success(response.message || 'Venues assigned successfully.')

        // Refresh metadata for all price files to update venue lists
        if (priceFiles && priceFiles.length > 0) {
          const refreshPromises = priceFiles.map(priceFile => {
            return dispatch(fetchPriceFileMetadata(priceFile._id))
          })
          await Promise.all(refreshPromises)
        }

        setVenueAssignmentModalVisible(false)
      } else {
        message.error(
          response.message || 'Failed to assign venues. Please try again.',
        )
      }
    } catch (error) {
      console.error('Failed to assign venues:', error)
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to assign venues. Please try again.'
      message.error(errorMessage)
    } finally {
      setIsVenueAssignmentSaving(false)
    }
  }

  const handleVenueAssignmentCancel = () => {
    setVenueAssignmentModalVisible(false)
    setVenueSearchTerm('')
    setVenueFilterType('all')
    if (selectedPriceFileId && priceFileMetadata[selectedPriceFileId]) {
      setSelectedVenuesForAssignment(
        priceFileMetadata[selectedPriceFileId].venues?.map(v => v._id) || [],
      )
    }
  }

  // Helper function to get price file label
  const getPriceFileLabel = priceFile => {
    const parts = [priceFile.title, priceFile.secondTitle].filter(Boolean)
    return parts.length > 0 ? parts.join(' ') : 'Unnamed Price File'
  }

  // Collect all unique venues from all price files' metadata
  const allVenues = useMemo(() => {
    const venueMap = new Map()
    Object.values(priceFileMetadata).forEach(meta => {
      if (meta.venues) {
        meta.venues.forEach(venue => {
          if (!venueMap.has(venue._id)) {
            venueMap.set(venue._id, venue)
          }
        })
      }
    })
    return Array.from(venueMap.values())
  }, [priceFileMetadata])

  // Build venue-to-price-file mapping: venueId → { priceFileId, priceFileName }
  const venueToPriceFileMap = useMemo(() => {
    const map = new Map()
    priceFiles.forEach(priceFile => {
      const metadata = priceFileMetadata[priceFile._id]
      if (metadata?.venues) {
        metadata.venues.forEach(venue => {
          if (!map.has(venue._id)) {
            map.set(venue._id, {
              priceFileId: priceFile._id,
              priceFileName: getPriceFileLabel(priceFile),
            })
          }
        })
      }
    })
    return map
  }, [priceFiles, priceFileMetadata])

  // Calculate which venues will be moved (currently on different price file)
  const venuesToMove = useMemo(() => {
    if (!selectedPriceFileId) return []
    return selectedVenuesForAssignment.filter(venueId => {
      const assignment = venueToPriceFileMap.get(venueId)
      return assignment && assignment.priceFileId !== selectedPriceFileId
    })
  }, [selectedVenuesForAssignment, selectedPriceFileId, venueToPriceFileMap])

  // Filter venues based on search term and filter type
  const filteredVenues = useMemo(() => {
    let filtered = allVenues

    // Filter by assignment type
    if (venueFilterType === 'onThisFile') {
      filtered = filtered.filter(venue => {
        const assignment = venueToPriceFileMap.get(venue._id)
        return assignment?.priceFileId === selectedPriceFileId
      })
    } else if (venueFilterType === 'onOtherFiles') {
      filtered = filtered.filter(venue => {
        const assignment = venueToPriceFileMap.get(venue._id)
        return assignment && assignment.priceFileId !== selectedPriceFileId
      })
    }

    // Filter by search term
    if (venueSearchTerm.trim()) {
      const searchLower = venueSearchTerm.toLowerCase().trim()
      filtered = filtered.filter(venue =>
        venue.title?.toLowerCase().includes(searchLower),
      )
    }

    return filtered
  }, [
    allVenues,
    venueSearchTerm,
    venueFilterType,
    venueToPriceFileMap,
    selectedPriceFileId,
  ])

  // Handle select all / deselect all for filtered venues
  const handleSelectAllFiltered = () => {
    const filteredVenueIds = filteredVenues
      .filter(venue => {
        const assignment = venueToPriceFileMap.get(venue._id)
        // Only include venues not already on current file (can't select disabled ones)
        return assignment?.priceFileId !== selectedPriceFileId
      })
      .map(venue => venue._id)

    setSelectedVenuesForAssignment(prev => {
      const newSelection = [...prev]
      filteredVenueIds.forEach(id => {
        if (!newSelection.includes(id)) {
          newSelection.push(id)
        }
      })
      return newSelection
    })
  }

  const handleDeselectAllFiltered = () => {
    const filteredVenueIds = filteredVenues.map(venue => venue._id)
    setSelectedVenuesForAssignment(prev =>
      prev.filter(id => !filteredVenueIds.includes(id)),
    )
  }

  // Get current price file name for display
  const currentPriceFileName = useMemo(() => {
    if (!selectedPriceFileId) return ''
    const priceFile = priceFiles.find(pf => pf._id === selectedPriceFileId)
    return priceFile ? getPriceFileLabel(priceFile) : ''
  }, [selectedPriceFileId, priceFiles])

  // Get metadata for selected price file
  const metadata = selectedPriceFileId
    ? priceFileMetadata[selectedPriceFileId]
    : null

  // Calculate days old
  const getDaysOld = lastUpdated => {
    if (!lastUpdated) return null
    return moment().diff(moment(lastUpdated), 'days')
  }

  // Get status color for last updated (green <30 days, red >=30 days)
  const getStatusColor = daysOld => {
    if (daysOld === null) return 'default'
    if (daysOld < 30) return 'green' // Use 'green' instead of 'success' for better visibility
    return 'red' // Use 'red' instead of 'error' for better visibility
  }

  const handleCreateNewPriceFile = () => {
    resetCreatePriceFileState()
    setIsCreatePriceFileModalVisible(true)
  }

  const handleDeleteProduct = async product => {
    if (!selectedPriceFileId) {
      message.warning('Please select a price file first.')
      return
    }

    setDeletingProductId(product._id)
    try {
      await dispatch(adminDeleteProduct(product))
      message.success('Product deleted successfully.')

      fetchProductsForCurrentView()

      dispatch(fetchPriceFileMetadata(selectedPriceFileId))
    } catch (error) {
      console.error('Failed to delete product:', error)
      message.error('Failed to delete product. Please try again.')
      setProductsLoading(false)
    } finally {
      setDeletingProductId(null)
    }
  }

  const handleSaveFileName = async () => {
    // Validate: can't be empty
    const trimmedName = editingFileName.trim()
    if (!trimmedName) {
      setFileNameError('File name cannot be empty')
      return
    }

    const selectedPriceFile = priceFiles.find(
      pf => pf._id === selectedPriceFileId,
    )
    if (!selectedPriceFile) {
      setFileNameError('Price file not found')
      return
    }

    setIsSavingFileName(true)
    setFileNameError('')

    try {
      const updatedSupplier = {
        ...selectedPriceFile,
        secondTitle: trimmedName,
      }

      const saved = await dispatch(editSupplier(updatedSupplier))
      if (saved === false) {
        setFileNameError('Failed to update file name. Please try again.')
        return
      }
      await dispatch(fetchPriceFiles()) // Refresh the list
      if (currentUser?.supplier?.length && saved?._id) {
        dispatch(
          setAccount({
            ...currentUser,
            supplier: currentUser.supplier.map(pf =>
              pf._id === saved._id ? { ...pf, ...saved } : pf,
            ),
          }),
        )
      }
      setIsEditFileNameModalVisible(false)
      setEditingFileName('')
      message.success('File name updated successfully')
    } catch (error) {
      console.error('Failed to update file name:', error)
      const errorMsg =
        error?.message || 'Failed to update file name. Please try again.'
      setFileNameError(errorMsg)
      // Modal stays open on error
    } finally {
      setIsSavingFileName(false)
    }
  }

  const handleOpenBulkModal = () => {
    setIsBulkModalVisible(true)
  }

  const handleCloseBulkModal = () => {
    setIsBulkModalVisible(false)
    setBulkFile(null)
    setBulkFileHeaders([])
    resetMapping()
    setBulkUploadKey(null)
    setBulkUploadBucket(null)
    setRemoveMissingProducts(false)
    setProjectedRemovals(null)
    setProjectedRemovalSkus([])
    setIsDeletionListVisible(false)
    setProjectedNewProducts(null)
    setProjectedNewProductSkus([])
    setIsNewProductListVisible(false)
    setBulkApplySummary(null)
    setIsBulkApplying(false)
    setBulkProgressMessage('')
    // Reset schedule state
    setIsScheduleEnabled(false)
    setScheduledDate(null)
    setScheduledTime(null)
    setScheduleError('')
  }

  // Fetch scheduled uploads from API (lazy loading - only when table is opened)
  const fetchScheduledUploads = useCallback(
    async supplierId => {
      if (
        hasFetchedScheduledUploads ||
        isLoadingScheduledUploads ||
        !currentUser
      ) {
        return
      }

      setIsLoadingScheduledUploads(true)
      try {
        const session = await Auth.currentAuthenticatedUser()
        const username = session?.attributes?.sub

        if (!username) {
          setIsLoadingScheduledUploads(false)
          return
        }

        const query = new URLSearchParams({ username })
        if (supplierId) {
          query.append('supplierId', supplierId)
        }
        const response = await API.get(
          'api',
          `supplier/scheduled-uploads?${query}`,
        )

        if (response && Array.isArray(response)) {
          // Convert date strings to moment objects for display
          const formattedUploads = response.map(upload => ({
            ...upload,
            scheduledFor: moment(upload.scheduledFor),
            createdAt: moment(upload.createdAt),
            appliedAt: upload.appliedAt ? moment(upload.appliedAt) : null,
          }))
          setScheduledUploads(formattedUploads)
          setHasFetchedScheduledUploads(true)
        }
      } catch (error) {
        console.error('Failed to fetch scheduled uploads:', error)
        // Don't show error to user, just log it
        // Set empty array to prevent issues
        setScheduledUploads([])
      } finally {
        setIsLoadingScheduledUploads(false)
      }
    },
    [currentUser, hasFetchedScheduledUploads, isLoadingScheduledUploads],
  )

  // Validate scheduled date/time
  const validateScheduleDateTime = (date, time) => {
    if (!date || !time) {
      return 'Please select both date and time.'
    }

    const now = moment()
    // Combine date and time
    const dateStr = date.format('YYYY-MM-DD')
    const timeStr = time.format('HH:mm')
    const scheduledMoment = moment(`${dateStr} ${timeStr}`, 'YYYY-MM-DD HH:mm')

    // Check if in the past
    if (scheduledMoment.isBefore(now)) {
      return 'Scheduled time must be in the future.'
    }

    // Check minimum advance time (1 minute for testing, 5 minutes for production)
    const minAdvanceMinutes = 1 // Change to 5 for production
    const diffMinutes = scheduledMoment.diff(now, 'minutes')
    if (diffMinutes < minAdvanceMinutes) {
      return `Scheduled time must be at least ${minAdvanceMinutes} minute${
        minAdvanceMinutes > 1 ? 's' : ''
      } in the future.`
    }

    // Check maximum advance time (1 year)
    const maxAdvanceDays = 365
    const diffDays = scheduledMoment.diff(now, 'days')
    if (diffDays > maxAdvanceDays) {
      return 'Scheduled time cannot be more than 1 year in the future.'
    }

    return ''
  }

  // Handle schedule toggle change
  const handleScheduleToggle = checked => {
    setIsScheduleEnabled(checked)
    if (!checked) {
      setScheduledDate(null)
      setScheduledTime(null)
      setScheduleError('')
    } else {
      // Set default to tomorrow at 9:00 AM
      const tomorrow = moment()
        .add(1, 'day')
        .hour(9)
        .minute(0)
        .second(0)
        .millisecond(0)
      setScheduledDate(moment(tomorrow))
      setScheduledTime(moment(tomorrow))
    }
  }

  // Handle scheduled date change
  const handleScheduledDateChange = date => {
    setScheduledDate(date)
    setScheduleError('')
    if (date && scheduledTime) {
      const error = validateScheduleDateTime(date, scheduledTime)
      setScheduleError(error)
    }
  }

  // Handle scheduled time change
  const handleScheduledTimeChange = time => {
    setScheduledTime(time)
    setScheduleError('')
    if (scheduledDate && time) {
      const error = validateScheduleDateTime(scheduledDate, time)
      setScheduleError(error)
    }
  }

  // Check for conflicting scheduled upload
  const checkForConflict = priceFileId => {
    const pendingUpload = scheduledUploads.find(
      upload =>
        upload.priceFileId === priceFileId && upload.status === 'pending',
    )
    return pendingUpload || null
  }

  // Handle scheduling an upload
  const handleScheduleUpload = () => {
    if (!selectedPriceFileId) {
      message.warning('Please select a price file first.')
      return false
    }

    if (!isScheduleEnabled) {
      // If schedule is disabled, proceed with immediate apply
      return false
    }

    // Validate date/time
    const error = validateScheduleDateTime(scheduledDate, scheduledTime)
    if (error) {
      setScheduleError(error)
      return false
    }

    // Check for conflict
    const conflict = checkForConflict(selectedPriceFileId)
    if (conflict) {
      setConflictingSchedule(conflict)
      setIsScheduleConflictModalVisible(true)
      return false
    }

    // Return true to indicate validation passed - actual scheduling happens in handleApplyBulkChanges
    return true
  }

  // Handle conflict confirmation
  const handleConfirmScheduleConflict = async () => {
    if (!conflictingSchedule) return

    try {
      // Cancel the conflicting upload via API
      const session = await Auth.currentAuthenticatedUser()
      const username = session?.attributes?.sub

      if (username) {
        await API.del(
          'api',
          `supplier/scheduled-uploads/${conflictingSchedule._id}`,
          {
            queryStringParameters: { username },
          },
        )
      }

      // Update local state
      setScheduledUploads(prev =>
        prev.map(upload =>
          upload._id === conflictingSchedule._id
            ? { ...upload, status: 'cancelled' }
            : upload,
        ),
      )
    } catch (error) {
      console.error('Failed to cancel conflicting scheduled upload:', error)
      // Continue anyway - backend will handle it
    }

    setIsScheduleConflictModalVisible(false)
    setConflictingSchedule(null)

    // Now proceed with scheduling (validation already passed)
    // The actual scheduling will happen in handleApplyBulkChanges
    handleApplyBulkChanges()
  }

  // Handle cancel/delete scheduled upload
  const handleCancelScheduledUpload = async uploadId => {
    try {
      const session = await Auth.currentAuthenticatedUser()
      const username = session?.attributes?.sub

      if (!username) {
        message.error('Unable to authenticate. Please try again.')
        return
      }

      // Append username as query parameter to the URL
      const response = await API.del(
        'api',
        `supplier/scheduled-uploads/${uploadId}?username=${encodeURIComponent(
          username,
        )}`,
      )

      if (response?.success) {
        // Refresh the scheduled uploads list
        await fetchScheduledUploads()
        message.success(response.message || 'Scheduled upload cancelled.')
      } else {
        throw new Error(
          response?.message || 'Failed to cancel scheduled upload',
        )
      }
    } catch (error) {
      console.error('Failed to cancel scheduled upload:', error)
      message.error(getErrorMessage(error))
    }
  }

  // Handle dismiss scheduled upload (for applied/cancelled)
  const handleDismissScheduledUpload = async uploadId => {
    // Optimistically remove from UI immediately
    const originalUploads = [...scheduledUploads]
    setScheduledUploads(prev => prev.filter(upload => upload._id !== uploadId))

    try {
      const session = await Auth.currentAuthenticatedUser()
      const username = session?.attributes?.sub

      if (!username) {
        // Restore on error
        setScheduledUploads(originalUploads)
        message.error('Unable to authenticate. Please try again.')
        return
      }

      const response = await API.patch(
        'api',
        `supplier/scheduled-uploads/${uploadId}/dismiss?username=${encodeURIComponent(
          username,
        )}`,
      )

      if (response?.success) {
        message.success(response.message || 'Upload dismissed.')
        // Refresh to ensure we have the latest data (though it should already be filtered)
        await fetchScheduledUploads()
      } else {
        // Restore on error
        setScheduledUploads(originalUploads)
        throw new Error(
          response?.message || 'Failed to dismiss scheduled upload',
        )
      }
    } catch (error) {
      // Restore on error
      setScheduledUploads(originalUploads)
      console.error('Failed to dismiss scheduled upload:', error)
      message.error(getErrorMessage(error))
    }
  }

  const resetMapping = () => {
    setBulkFieldMapping(
      bulkMappingFields.reduce((acc, field) => {
        acc[field.key] = ''
        return acc
      }, {}),
    )
    setHasUserTouchedMapping(false)
    setAutoFilledFields(new Set())
    setMappingLastUsedAt(null)
  }

  const uploadProps = {
    name: 'file',
    multiple: false,
    customRequest: ({ onSuccess }) => {
      // Prevent default upload - we handle it manually in beforeUpload
      onSuccess('ok')
    },
    beforeUpload: async file => {
      try {
        if (!selectedPriceFileId) {
          message.warning('Please choose a price file first.')
          return Upload.LIST_IGNORE
        }
        setBulkFile(file)
        setIsMappingScanning(true)
        setBulkApplySummary(null)
        setProjectedRemovals(null)
        setProjectedRemovalSkus([])
        setProjectedNewProducts(null)
        setProjectedNewProductSkus([])
        setIsValidationErrorModalVisible(false)
        setValidationErrors([])
        resetMapping()
        const uploadResult = await uploadFile(file)
        if (!uploadResult?.keyName) {
          throw new Error('Upload failed. Please try again.')
        }
        setBulkUploadKey(uploadResult.keyName)
        setBulkUploadBucket(
          uploadResult.bucketName || 'invoices-ptotos-staging',
        )

        // Fetch headers by asking backend to scan the uploaded file
        const session = await Auth.currentAuthenticatedUser()
        const response = await API.post(
          'api',
          `supplier/${selectedPriceFileId}/price-file-upload`,
          {
            body: {
              fileKey: uploadResult.keyName,
              bucketName: uploadResult.bucketName || 'invoices-ptotos-staging',
              username: session?.attributes?.sub,
            },
          },
        )

        // Handle different possible response structures
        const headers =
          response?.headers ||
          response?.data?.headers ||
          response?.body?.headers ||
          []
        setBulkFileHeaders(headers)
        setIsMappingScanning(false)
        setHasUserTouchedMapping(false) // Reset when new file is uploaded
        setAutoFilledFields(new Set())
        setMappingLastUsedAt(null)
        message.success('File uploaded. Please map your columns.')
      } catch (error) {
        console.error(error)
        message.error(getErrorMessage(error))
        setBulkFile(null)
        setBulkUploadKey(null)
        setBulkUploadBucket(null)
        setIsMappingScanning(false)
      }
      return false
    },
    onRemove: () => {
      setBulkFile(null)
      setBulkUploadKey(null)
      setBulkUploadBucket(null)
      setBulkFileHeaders([])
      setProjectedRemovals(null)
      setProjectedRemovalSkus([])
      setIsDeletionListVisible(false)
      setProjectedNewProducts(null)
      setProjectedNewProductSkus([])
      setIsNewProductListVisible(false)
      setBulkApplySummary(null)
      setIsValidationErrorModalVisible(false)
      setValidationErrors([])
      setHasUserTouchedMapping(false)
      setAutoFilledFields(new Set())
      setMappingLastUsedAt(null)
      resetMapping()
    },
    fileList: bulkFile ? [bulkFile] : [],
    showUploadList: {
      showRemoveIcon: !isBulkApplying,
      showPreviewIcon: false,
    },
  }

  const handleMappingChange = (fieldKey, value) => {
    setHasUserTouchedMapping(true) // Mark that user has manually changed a mapping

    // Remove auto-fill badge if user changes this field
    setAutoFilledFields(prev => {
      const newSet = new Set(prev)
      newSet.delete(fieldKey)
      return newSet
    })

    setBulkFieldMapping(prev => ({
      ...prev,
      [fieldKey]: value,
    }))
  }

  const handleRemoveMissingToggle = e => {
    setRemoveMissingProducts(e.target.checked)
    if (!e.target.checked) {
      setProjectedRemovals(null)
      setProjectedRemovalSkus([])
      setIsDeletionListVisible(false)
    }
  }

  const handleToggleDeletionList = async () => {
    if (!projectedRemovals || projectedRemovals === 0) {
      message.info('There are no products scheduled for deletion.')
      return
    }
    if (isDeletionListVisible) {
      setIsDeletionListVisible(false)
      return
    }
    await runDeletionPreview(true)
    setIsDeletionListVisible(true)
  }

  const handleToggleNewProductList = async () => {
    if (!projectedNewProducts || projectedNewProducts === 0) {
      message.info('There are no new products to be created.')
      return
    }
    if (isNewProductListVisible) {
      setIsNewProductListVisible(false)
      return
    }
    await runNewProductPreview(true)
    setIsNewProductListVisible(true)
  }

  const handleApplyBulkChanges = async () => {
    if (!bulkUploadKey || !selectedPriceFileId) {
      message.error('Please upload a valid file before applying changes.')
      return
    }

    if (isMappingIncomplete) {
      message.warning('Please map all required fields first.')
      return
    }

    // Check if scheduling is enabled
    let scheduledFor = null
    if (isScheduleEnabled) {
      const validationPassed = handleScheduleUpload()
      if (!validationPassed) {
        return // Validation failed or conflict modal shown
      }

      // Format scheduledFor for API: "YYYY-MM-DD HH:mm"
      const dateStr = scheduledDate.format('YYYY-MM-DD')
      const timeStr = scheduledTime.format('HH:mm')
      scheduledFor = `${dateStr} ${timeStr}`
    }

    // If there's a pending scheduled upload and we're applying immediately, cancel it
    if (!scheduledFor) {
      const conflict = checkForConflict(selectedPriceFileId)
      if (conflict) {
        setScheduledUploads(prev =>
          prev.map(upload =>
            upload._id === conflict._id
              ? { ...upload, status: 'cancelled' }
              : upload,
          ),
        )
        message.info(
          'Pending scheduled upload cancelled. Applying changes immediately.',
        )
      }
    }

    try {
      setIsBulkApplying(true)
      setBulkProgressMessage(
        scheduledFor ? 'Scheduling upload...' : 'Applying updates...',
      )
      const session = await Auth.currentAuthenticatedUser()

      const requestBody = {
        fileKey: bulkUploadKey,
        bucketName: bulkUploadBucket,
        mapping: bulkFieldMapping,
        headers: bulkFileHeaders, // Pass headers for saving mapping
        removeMissing: removeMissingProducts,
        username: session?.attributes?.sub,
      }

      // Add scheduledFor if scheduling is enabled
      if (scheduledFor) {
        requestBody.scheduledFor = scheduledFor
      }

      const response = await API.post(
        'api',
        `supplier/${selectedPriceFileId}/price-file-apply`,
        {
          body: requestBody,
        },
      )

      // If scheduled, refresh scheduled uploads list and show success
      if (scheduledFor && response?.success) {
        message.success(response.message || 'Upload scheduled successfully')
        // Refresh scheduled uploads list
        setHasFetchedScheduledUploads(false)
        if (!isScheduledUploadsCollapsed) {
          fetchScheduledUploads()
        }
        setBulkProgressMessage('')
        setIsBulkApplying(false)
        handleCloseBulkModal()
        return
      }

      setBulkApplySummary({
        updated: response.updated || 0,
        created: response.created || 0,
        deleted: response.deleted || 0,
        newSkusCount: response.newSkusCount || 0,
      })
      message.success('Price file updated successfully')
      setBulkProgressMessage('Refreshing data...')
      fetchProductsForCurrentView()
      dispatch(fetchPriceFileMetadata(selectedPriceFileId))
      try {
        await API.get(
          'api',
          `admin/search/indexProducts/${selectedPriceFileId}`,
        )
        message.success('Refreshing catalogue search results…')
      } catch (reindexError) {
        console.error(
          'Failed to reindex search after bulk update:',
          reindexError,
        )
        Sentry.captureException(reindexError)
        message.warning(
          'Price file updated, but the catalogue search index could not be refreshed automatically. Please trigger a manual reindex.',
        )
      }
      setBulkProgressMessage('')
      setIsBulkApplying(false)
      handleCloseBulkModal()
    } catch (error) {
      console.error('Bulk apply error:', error)
      console.error('Error response:', error?.response)
      const errorMsg = getErrorMessage(error)

      if (isValidationErrorMessage(errorMsg)) {
        showValidationErrors(errorMsg)
      } else {
        message.error({
          content: errorMsg,
          duration: 8,
          style: { marginTop: '20vh' },
        })
      }

      setIsBulkApplying(false)
      setBulkProgressMessage('')
    }
  }

  // Products are fetched per page and filtered/searched server-side, so the
  // store already holds exactly what should be displayed for this view
  const displayProducts = useMemo(() => {
    if (!selectedPriceFileId) return []
    return products || []
  }, [selectedPriceFileId, products])

  const openEditModal = product => {
    setEditedProduct(product)
    setIsEditModalVisible(true)
  }

  const closeEditModal = () => {
    setIsEditModalVisible(false)
    setEditedProduct(null)
  }

  const handleSaveEditedProduct = async () => {
    if (!editedProduct) return
    setIsEditSaving(true)

    // Auto-sync OMSPrice to match customer price for supplier edits
    const productToSave = {
      ...editedProduct,
      OMSPrice: editedProduct.price,
    }

    const result = await dispatch(adminEditProduct(productToSave))
    if (result === false) {
      message.error('Unable to update product. Please try again.')
      setIsEditSaving(false)
      return
    }

    message.success('Product updated')
    closeEditModal()
    if (selectedPriceFileId) {
      fetchProductsForCurrentView()
    }
    setIsEditSaving(false)
  }

  const tableColumns = [
    {
      title: '',
      dataIndex: 'cloudinaryImageId',
      key: 'cloudinaryImageId',
      width: 80,
      render: () => (
        <ImagePlaceholder>
          <FileTextOutlined style={{ fontSize: 24, color: '#d9d9d9' }} />
        </ImagePlaceholder>
      ),
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      sorter: (a, b) =>
        String(a.title).trim().localeCompare(String(b.title).trim()),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Size',
      dataIndex: 'size',
      key: 'size',
      sorter: (a, b) =>
        String(a.size).trim().localeCompare(String(b.size).trim()),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Customer Price',
      dataIndex: 'price',
      key: 'price',
      render: text => `£${Number(text).toFixed(2)}`,
      sorter: (a, b) => a.price - b.price,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Priced By',
      dataIndex: 'pricedBy',
      key: 'pricedBy',
      render: pricedBy => pricedBy || '-',
    },
    {
      title: 'Has VAT',
      dataIndex: 'hasVat',
      key: 'hasVat',
      render: hasVat => (hasVat ? 'Yes' : 'No'),
    },
    {
      title: 'GLN',
      dataIndex: 'GLN',
      key: 'GLN',
      render: gln => gln || '-',
    },
    {
      title: 'SKU',
      dataIndex: 'sku',
      key: 'sku',
      sorter: (a, b) =>
        String(a.sku).trim().localeCompare(String(b.sku).trim()),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      sorter: (a, b) =>
        String(a.type).trim().localeCompare(String(b.type).trim()),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <ActionButtons>
          <Button
            type='dashed'
            size='small'
            onClick={() => openEditModal(record)}
          >
            Edit
          </Button>
          {showPriceAudit ? (
            <Button
              type='default'
              size='small'
              onClick={() => priceAudit.openAudit(record)}
            >
              Audit
            </Button>
          ) : null}
          <Popconfirm
            title='Are you sure you want to delete this product?'
            okText='Yes'
            cancelText='No'
            onConfirm={() => handleDeleteProduct(record)}
          >
            <Button
              size='small'
              loading={deletingProductId === record._id}
              style={{
                backgroundColor: '#ff4d4f',
                borderColor: '#ff4d4f',
                color: '#ffffff',
              }}
            >
              Delete
            </Button>
          </Popconfirm>
        </ActionButtons>
      ),
    },
  ]

  return (
    <Wrapper>
      <HeaderSection>
        <Title>Product Files</Title>
        <CreateNewPriceFileButton
          type='primary'
          onClick={handleCreateNewPriceFile}
        >
          <PlusOutlined style={{ marginRight: 6 }} />
          Create New Price File
        </CreateNewPriceFileButton>
      </HeaderSection>

      <ScheduledUploadsSection
        selectedPriceFileId={selectedPriceFileId}
        scheduledUploads={scheduledUploads}
        isLoading={isLoadingScheduledUploads}
        isCollapsed={isScheduledUploadsCollapsed}
        hasFetched={hasFetchedScheduledUploads}
        onToggleCollapsed={setIsScheduledUploadsCollapsed}
        onFetch={fetchScheduledUploads}
        onCancelUpload={handleCancelScheduledUpload}
        onDismissUpload={handleDismissScheduledUpload}
      />

      {/* Info Banner */}
      <InfoBanner
        message="Price files are guides only. You charge customers their contracted prices, not the prices in these files. OMS doesn't manage your pricing."
        type='info'
        showIcon
        closable
        style={{ marginBottom: 16 }}
      />

      {/* Integration Advert */}
      {!isAdvertDismissed && (
        <IntegrationAdvertCard style={{ marginBottom: 16 }}>
          <IntegrationAdvertContent>
            <IntegrationAdvertIcon>
              <MailOutlined style={{ fontSize: 18, color: '#1890ff' }} />
            </IntegrationAdvertIcon>
            <IntegrationAdvertText>
              <IntegrationAdvertTitle>
                Free Pricing Integration
              </IntegrationAdvertTitle>
              <IntegrationAdvertDescription>
                We can integrate your pricing for free! Your catalogues will be
                automatically sent to OMS.
              </IntegrationAdvertDescription>
              <IntegrationAdvertLink href='mailto:info@ordersmadesimple.com'>
                Get in touch
              </IntegrationAdvertLink>
            </IntegrationAdvertText>
            <CloseButton onClick={() => setIsAdvertDismissed(true)}>
              <CloseOutlined />
            </CloseButton>
          </IntegrationAdvertContent>
        </IntegrationAdvertCard>
      )}

      {/* Educational Content */}
      <EducationalCard style={{ marginBottom: 24 }}>
        <Collapse
          activeKey={educationalContentExpanded ? ['1'] : []}
          onChange={keys => setEducationalContentExpanded(keys.includes('1'))}
        >
          <Panel
            header={
              <EducationalHeader>
                <QuestionCircleOutlined
                  style={{ marginRight: 8, fontSize: 18 }}
                />
                How Price Files Work on OMS
              </EducationalHeader>
            }
            key='1'
          >
            <EducationalContent>
              <EducationalSection>
                <EducationalSectionTitle>
                  Understanding Price Files
                </EducationalSectionTitle>
                <EducationalText>
                  On OMS, price files are standalone product and price lists.
                  Unlike other systems where you create products and assign
                  different prices to each customer, OMS works differently:
                </EducationalText>
                <EducationalList>
                  <li>
                    Each price file is a complete product list with its own
                    prices
                  </li>
                  <li>
                    To offer different prices, you create a separate price file
                  </li>
                  <li>
                    Multiple venues can be assigned to the same price file
                  </li>
                  <li>Each venue can only be on one price file at a time</li>
                </EducationalList>
              </EducationalSection>

              <EducationalSection>
                <EducationalSectionTitle>
                  How to Manage Price Files
                </EducationalSectionTitle>
                <EducationalText>
                  Upload your price file in your own format (CSV or Excel).
                  We&apos;ll scan your column headers and automatically map them
                  to our fields. You can then review and adjust the mapping if
                  needed.
                </EducationalText>
                <EducationalSteps>
                  <EducationalStep>
                    <strong>Step 1:</strong> Upload your file (CSV or Excel
                    format) - use your existing file structure, no need to match
                    our format.
                  </EducationalStep>
                  <EducationalStep>
                    <strong>Step 2:</strong> We automatically scan your column
                    headers and suggest mappings to our fields. Review and
                    adjust the mapping as needed.
                  </EducationalStep>
                  <EducationalStep>
                    <strong>Step 3:</strong> Products are matched by SKU. If we
                    recognize a SKU, we&apos;ll update that product. If we
                    don&apos;t recognize a SKU, we&apos;ll add it as a new
                    product. <strong>Important:</strong> Make sure your SKUs are
                    correct!
                  </EducationalStep>
                </EducationalSteps>
              </EducationalSection>

              <EducationalSection>
                <EducationalSectionTitle>
                  Pro Tip: £0 Price File
                </EducationalSectionTitle>
                <EducationalText>
                  Create a £0 price file with products but no prices. This is
                  perfect for inviting new customers - they won&apos;t see
                  pricing, making it easier to onboard them. Once they start
                  ordering or ask to see prices, you can switch them to a priced
                  file.
                </EducationalText>
              </EducationalSection>

              <EducationalSection>
                <EducationalSectionTitle>
                  Assigning Venues to Price Files
                </EducationalSectionTitle>
                <EducationalText>
                  Start by creating a price file and adding venues to it. You
                  can assign multiple venues to one price file, but each venue
                  can only be on one price file at a time. To change a
                  venue&apos;s pricing, switch them to a different price file.
                </EducationalText>
              </EducationalSection>
            </EducationalContent>
          </Panel>
        </Collapse>
      </EducationalCard>

      <Card style={{ marginBottom: 24 }}>
        <PriceFileSelectorContainer>
          <Label>Select Price File</Label>
          <Select
            style={{ width: '100%', maxWidth: 500 }}
            placeholder='Choose a price file to view its products'
            value={selectedPriceFileId}
            onChange={handlePriceFileChange}
            allowClear
            loading={isPriceFilesLoading}
          >
            {priceFiles.map(priceFile => (
              <Option key={priceFile._id} value={priceFile._id}>
                {getPriceFileLabel(priceFile)}
              </Option>
            ))}
          </Select>
        </PriceFileSelectorContainer>
      </Card>

      {(() => {
        const selectedPriceFile = priceFiles.find(
          pf => pf._id === selectedPriceFileId,
        )
        const isIronWeb =
          String(selectedPriceFile?.integrationType || '')
            .trim()
            .toLowerCase() === 'ironweb'
        if (!selectedPriceFile || !isIronWeb) return null
        return (
          <Card style={{ marginBottom: 24 }}>
            <IronWebCatalogueStatus priceFileId={selectedPriceFile._id} />
          </Card>
        )
      })()}

      {selectedPriceFileId && (
        <MetadataCard style={{ marginBottom: 24 }}>
          {isMetadataLoading ? (
            <LoadingState>
              <Spin size='large' />
            </LoadingState>
          ) : metadata ? (
            <>
              <MetadataHeader>
                <InfoCircleOutlined style={{ fontSize: 20, marginRight: 8 }} />
                <MetadataTitle>Quick Stats</MetadataTitle>
              </MetadataHeader>
              <MetadataContent>
                <MetadataItem>
                  <MetadataLabel>
                    <FileTextOutlined style={{ marginRight: 4 }} />
                    File Name:
                  </MetadataLabel>
                  <MetadataValue>
                    {(() => {
                      const selectedPriceFile = priceFiles.find(
                        pf => pf._id === selectedPriceFileId,
                      )
                      const fileName =
                        selectedPriceFile?.secondTitle || 'Unnamed Price File'
                      return (
                        <>
                          {fileName}
                          <Button
                            type='text'
                            size='small'
                            onClick={e => {
                              e.preventDefault()
                              e.stopPropagation()
                              const fileName =
                                selectedPriceFile?.secondTitle || ''
                              setEditingFileName(fileName)
                              setFileNameError('')
                              setIsEditFileNameModalVisible(true)
                            }}
                            style={{ marginLeft: 8 }}
                          >
                            Edit Name
                          </Button>
                        </>
                      )
                    })()}
                  </MetadataValue>
                </MetadataItem>
                <MetadataItem>
                  <MetadataLabel>
                    <CalendarOutlined style={{ marginRight: 4 }} />
                    Last Updated:
                  </MetadataLabel>
                  <MetadataValue>
                    {metadata.lastUpdated ? (
                      <>
                        {moment(metadata.lastUpdated).format('DD MMM YYYY')}
                        <Tooltip
                          title={
                            getDaysOld(metadata.lastUpdated) < 30
                              ? 'File was updated less than 30 days ago (green = recent, red = older than 30 days)'
                              : 'File was updated more than 30 days ago (green = recent, red = older than 30 days)'
                          }
                        >
                          <Tag
                            color={getStatusColor(
                              getDaysOld(metadata.lastUpdated),
                            )}
                            style={{ marginLeft: 8, cursor: 'help' }}
                          >
                            {getDaysOld(metadata.lastUpdated)} days ago
                          </Tag>
                        </Tooltip>
                      </>
                    ) : (
                      <Tag color='default'>(new)</Tag>
                    )}
                  </MetadataValue>
                </MetadataItem>
                {currentUser && (currentUser.name || currentUser.emailAddress) && (
                  <MetadataItem>
                    <MetadataLabel>
                      <UserOutlined style={{ marginRight: 4 }} />
                      Last Updated By:
                    </MetadataLabel>
                    <MetadataValue>
                      {currentUser.name || 'Unknown User'}
                      {currentUser.emailAddress && (
                        <span
                          style={{
                            marginLeft: 8,
                            color: '#666',
                            fontSize: '12px',
                          }}
                        >
                          ({currentUser.emailAddress})
                        </span>
                      )}
                    </MetadataValue>
                  </MetadataItem>
                )}
                <MetadataItem>
                  <MetadataLabel>Products:</MetadataLabel>
                  <MetadataValue>{metadata.productCount}</MetadataValue>
                </MetadataItem>
                <MetadataItem>
                  <MetadataLabel>Order email address:</MetadataLabel>
                  <MetadataValue>
                    {metadata.emailAddress || 'Not set'}
                  </MetadataValue>
                </MetadataItem>
                <MetadataItem>
                  <MetadataLabel>Contact number:</MetadataLabel>
                  <MetadataValue>
                    {metadata.phoneNumber || 'Not set'}
                  </MetadataValue>
                </MetadataItem>
                <MetadataItem>
                  <MetadataLabel>Minimum order value:</MetadataLabel>
                  <MetadataValue>
                    {typeof metadata.minOrderValue === 'number'
                      ? `£${(metadata.minOrderValue / 100).toFixed(2)}`
                      : '£0.00'}
                  </MetadataValue>
                </MetadataItem>
                <MetadataItem>
                  <MetadataLabel>Delivery days:</MetadataLabel>
                  <MetadataValue>
                    {Array.isArray(metadata.deliveryDays) &&
                    metadata.deliveryDays.length > 0
                      ? formatDeliveryDays(metadata.deliveryDays).join(', ')
                      : 'Not set'}
                  </MetadataValue>
                </MetadataItem>
                <MetadataItem>
                  <MetadataLabel>Cut-off time:</MetadataLabel>
                  <MetadataValue>
                    {metadata.orderCutOffTime || 'Not set'}
                  </MetadataValue>
                </MetadataItem>
                <MetadataItem>
                  <MetadataLabel>
                    <ShopOutlined style={{ marginRight: 4 }} />
                    Linked Venues:
                  </MetadataLabel>
                  <MetadataValue>
                    {metadata.venues.length}
                    {metadata.venues.length > 0 && (
                      <VenueToggle
                        onClick={() => setExpandedVenues(!expandedVenues)}
                      >
                        {expandedVenues ? ' Hide' : ' Show'} venues
                      </VenueToggle>
                    )}
                  </MetadataValue>
                </MetadataItem>
                {expandedVenues && metadata.venues.length > 0 && (
                  <VenueList>
                    {metadata.venues.map(venue => (
                      <VenueItem key={venue._id}>{venue.title}</VenueItem>
                    ))}
                  </VenueList>
                )}
                <MetadataItem>
                  <MetadataActions>
                    <Button
                      type='primary'
                      onClick={handleOpenBulkModal}
                      style={{
                        marginRight: 8,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <EditOutlined style={{ marginRight: 6 }} />
                      Edit Whole Price File
                    </Button>
                    <Button
                      onClick={handleDownloadCSV}
                      loading={csvLoading}
                      disabled={!selectedPriceFileId || csvLoading}
                      style={{ marginRight: 8 }}
                    >
                      <DownloadOutlined style={{ marginRight: 6 }} />
                      Download CSV
                    </Button>
                    <CSVLink
                      ref={csvButtonRef}
                      data={csvProducts}
                      filename={
                        selectedPriceFileId &&
                        priceFiles.find(pf => pf._id === selectedPriceFileId)
                          ? `oms-products-${getPriceFileLabel(
                              priceFiles.find(
                                pf => pf._id === selectedPriceFileId,
                              ),
                            )
                              .toLowerCase()
                              .replace(/\s+/g, '-')}_${moment().format(
                              'YYYY-MM-DD',
                            )}.csv`
                          : `oms-products-export_${moment().format(
                              'YYYY-MM-DD',
                            )}.csv`
                      }
                      style={{ display: 'none' }}
                    />
                    <Button
                      onClick={handleVenueAssignmentOpen}
                      style={{ marginRight: 8 }}
                    >
                      <UserOutlined style={{ marginRight: 6 }} />
                      Assign Venues
                    </Button>
                    <Button onClick={openEditSettingsModal}>
                      Edit file settings
                    </Button>
                  </MetadataActions>
                </MetadataItem>
              </MetadataContent>
            </>
          ) : (
            <LoadingState>
              No metadata found for this price file yet.
            </LoadingState>
          )}
        </MetadataCard>
      )}

      {selectedPriceFileId && (
        <>
          <SearchCard style={{ marginBottom: 24 }}>
            <Row gutter={16} align='middle'>
              <Col flex='auto'>
                <Search
                  placeholder='Search by...'
                  prefix={<SearchOutlined />}
                  value={searchTerm}
                  onChange={e => {
                    setSearchTerm(e.target.value)
                    setPaginationPage(1)
                  }}
                  allowClear
                  style={{ width: '100%' }}
                />
              </Col>
              <Col>
                <Checkbox.Group
                  options={[
                    { label: 'Title', value: 'title' },
                    { label: 'SKU', value: 'sku' },
                  ]}
                  value={searchFields}
                  onChange={values => {
                    setSearchFields(values)
                    setPaginationPage(1)
                  }}
                />
              </Col>
            </Row>
          </SearchCard>

          {productsLoading ? (
            <Card>
              <Spin
                size='large'
                style={{
                  display: 'block',
                  textAlign: 'center',
                  padding: '40px',
                }}
              />
            </Card>
          ) : displayProducts.length > 0 ? (
            <Table
              scroll={{ x: true }}
              dataSource={displayProducts.map(product => ({
                key: product._id,
                ...product,
              }))}
              columns={tableColumns}
              pagination={{
                current: paginationPage,
                pageSize: paginationSize,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100', '250', '500'],
                total: totalProducts,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`,
                onChange: (page, size) => {
                  const numericSize = Number(size)
                  if (numericSize !== paginationSize) {
                    setPaginationSize(numericSize)
                    setPaginationPage(1)
                  } else {
                    setPaginationPage(page)
                  }
                },
                onShowSizeChange: (_current, size) => {
                  const numericSize = Number(size)
                  if (numericSize !== paginationSize) {
                    setPaginationSize(numericSize)
                    setPaginationPage(1)
                  }
                },
              }}
            />
          ) : (
            <Card>
              <Empty
                description='No products found'
                image={
                  <FileTextOutlined
                    style={{ fontSize: 48, color: '#d9d9d9' }}
                  />
                }
              />
            </Card>
          )}
        </>
      )}

      {!selectedPriceFileId && <EmptyState />}

      {/* Edit Product Modal */}
      <Modal
        title='Edit Product'
        visible={isEditModalVisible}
        onOk={handleSaveEditedProduct}
        onCancel={closeEditModal}
        okText='Save'
        cancelText='Cancel'
        confirmLoading={isEditSaving}
        width={600}
        destroyOnClose
      >
        {editedProduct && (
          <EditProductModal
            product={editedProduct}
            setEditedProduct={setEditedProduct}
            hideOMSPrice={true}
          />
        )}
      </Modal>

      <BulkPriceFileEditModal
        selectedPriceFileId={selectedPriceFileId}
        onDownloadCurrentFile={handleDownloadCSV}
        isDownloadingCurrentFile={csvLoading}
        hook={{
          bulkMappingFields,
          isBulkModalVisible,
          closeBulkModal: handleCloseBulkModal,
          resetBulkState: handleCloseBulkModal,
          bulkFile,
          bulkFileHeaders,
          bulkFieldMapping,
          isMappingScanning,
          isMappingIncomplete,
          uploadProps,
          handleMappingChange,
          validationErrors,
          isValidationErrorModalVisible,
          setIsValidationErrorModalVisible,
          autoFilledFields,
          mappingLastUsedAt,
          removeMissingProducts,
          handleRemoveMissingToggle,
          projectedRemovals,
          projectedRemovalSkus,
          isDeletionListVisible,
          isFetchingDeletionSkus,
          handleToggleDeletionList,
          projectedNewProducts,
          projectedNewProductSkus,
          isNewProductListVisible,
          isFetchingNewProductSkus,
          handleToggleNewProductList,
          isBulkApplying,
          bulkProgressMessage,
          bulkApplySummary,
          handleApplyBulkChanges,
          isGuideExpanded,
          setIsGuideExpanded,
          isScheduleEnabled,
          handleScheduleToggle,
          scheduledDate,
          handleScheduledDateChange,
          scheduledTime,
          handleScheduledTimeChange,
          scheduleError,
          isScheduleConflictModalVisible,
          setIsScheduleConflictModalVisible,
          conflictingSchedule,
          handleConfirmScheduleConflict,
          resetMapping,
        }}
      />

      <ProductPriceAuditModal
        visible={priceAudit.visible}
        onClose={priceAudit.closeAudit}
        product={priceAudit.product}
        priceHistory={priceAudit.priceHistory}
        loading={priceAudit.loading}
        error={priceAudit.error}
      />

      {/* Edit File Name Modal */}
      <Modal
        title='Change file name'
        visible={isEditFileNameModalVisible}
        onCancel={() => {
          setIsEditFileNameModalVisible(false)
          setEditingFileName('')
          setFileNameError('')
        }}
        footer={[
          <Button
            key='cancel'
            onClick={() => {
              setIsEditFileNameModalVisible(false)
              setEditingFileName('')
              setFileNameError('')
            }}
            disabled={isSavingFileName}
          >
            Cancel
          </Button>,
          <Button
            key='save'
            type='primary'
            onClick={handleSaveFileName}
            loading={isSavingFileName}
          >
            Save
          </Button>,
        ]}
        width={500}
      >
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
            File Name:
          </label>
          <Input
            value={editingFileName}
            onChange={e => {
              setEditingFileName(e.target.value)
              setFileNameError('') // Clear error when user types
            }}
            placeholder='Enter file name'
            onPressEnter={handleSaveFileName}
            disabled={isSavingFileName}
            status={fileNameError ? 'error' : ''}
          />
          {fileNameError && (
            <Alert
              type='error'
              message={fileNameError}
              style={{ marginTop: 8 }}
              showIcon
            />
          )}
        </div>
      </Modal>

      {/* Edit File Settings Modal */}
      <Modal
        title='Edit file settings'
        visible={isEditSettingsModalVisible}
        onCancel={resetEditSettingsState}
        footer={[
          <Button key='cancel' onClick={resetEditSettingsState}>
            Cancel
          </Button>,
          <Button key='save' type='primary' onClick={handleSaveEditSettings}>
            Save settings
          </Button>,
        ]}
        width={600}
      >
        <Form layout='vertical'>
          <Form.Item label='Supplier name (title)'>
            <Input value={getBaseSupplierInfo().title} disabled />
          </Form.Item>
          <Form.Item
            label={
              <span>
                Price file name <RequiredPill>Required</RequiredPill>
              </span>
            }
            required
            validateStatus={
              editSettingsErrors.secondTitle ? 'error' : undefined
            }
            help={editSettingsErrors.secondTitle}
          >
            <Input
              placeholder='e.g. Original Price File, Hotels – London'
              value={editSettingsForm.secondTitle}
              onChange={e =>
                setEditSettingsForm(prev => ({
                  ...prev,
                  secondTitle: e.target.value,
                }))
              }
            />
          </Form.Item>
          <Form.Item
            label={
              <span>
                Order email address <RequiredPill>Required</RequiredPill>
              </span>
            }
            required
            validateStatus={editSettingsErrors.orderEmail ? 'error' : undefined}
            help={editSettingsErrors.orderEmail}
          >
            <Input
              placeholder='Where orders from this price file will be sent'
              value={editSettingsForm.orderEmail}
              onChange={e =>
                setEditSettingsForm(prev => ({
                  ...prev,
                  orderEmail: e.target.value,
                }))
              }
            />
          </Form.Item>
          <Form.Item
            label={
              <span>
                Contact number <RequiredPill>Required</RequiredPill>
              </span>
            }
            required
            validateStatus={
              editSettingsErrors.contactNumber ? 'error' : undefined
            }
            help={editSettingsErrors.contactNumber}
          >
            <Input
              placeholder='Shown to venues for questions about their orders'
              value={editSettingsForm.contactNumber}
              onChange={e =>
                setEditSettingsForm(prev => ({
                  ...prev,
                  contactNumber: e.target.value,
                }))
              }
            />
          </Form.Item>
          <Form.Item
            label='Minimum order value'
            validateStatus={
              editSettingsErrors.minOrderValue ? 'error' : undefined
            }
            help={
              editSettingsErrors.minOrderValue ||
              'Optional – leave blank for no minimum.'
            }
          >
            <Input
              placeholder='e.g. 100'
              value={editSettingsForm.minOrderValue}
              onChange={e =>
                setEditSettingsForm(prev => ({
                  ...prev,
                  minOrderValue: e.target.value,
                }))
              }
            />
          </Form.Item>
          <Form.Item
            label={
              <span>
                Delivery days <RequiredPill>Required</RequiredPill>
              </span>
            }
            required
            validateStatus={
              editSettingsErrors.deliveryDays ? 'error' : undefined
            }
            help={
              editSettingsErrors.deliveryDays ||
              'At least one delivery day is required.'
            }
          >
            <DeliveryDaysSelect
              deliveryDays={editSettingsForm.deliveryDays}
              onChange={value =>
                setEditSettingsForm(prev => ({
                  ...prev,
                  deliveryDays: value,
                }))
              }
              isDisabled={false}
            />
          </Form.Item>
          <Form.Item label='Cut-off time (optional)'>
            <Input
              placeholder='e.g. 16:00'
              value={editSettingsForm.cutOffTime}
              onChange={e =>
                setEditSettingsForm(prev => ({
                  ...prev,
                  cutOffTime: e.target.value,
                }))
              }
            />
            <BulkHelperText>
              Orders placed after this time will roll to the next available
              delivery day.
            </BulkHelperText>
          </Form.Item>
        </Form>
      </Modal>

      {/* Venue Assignment Modal */}
      <Modal
        title={`Assign Venues to Price File`}
        visible={venueAssignmentModalVisible}
        onOk={handleVenueAssignmentSave}
        onCancel={handleVenueAssignmentCancel}
        okText={
          venuesToMove.length > 0
            ? `Move ${venuesToMove.length} venue${
                venuesToMove.length > 1 ? 's' : ''
              } to this price file`
            : 'No changes to save'
        }
        okButtonProps={{
          disabled: venuesToMove.length === 0 || isVenueAssignmentSaving,
          loading: isVenueAssignmentSaving,
        }}
        cancelButtonProps={{
          disabled: isVenueAssignmentSaving,
        }}
        cancelText='Cancel'
        width={800}
        closable={!isVenueAssignmentSaving}
      >
        <VenueAssignmentContent>
          <VenueAssignmentInfo>
            <strong>Assigning venues to: {currentPriceFileName}</strong>
            <br />
            Each venue can only be on one price file at a time. Selecting a
            venue from another price file will move it here.
          </VenueAssignmentInfo>

          {allVenues.length === 0 ? (
            <Empty
              description='No venues found across your price files'
              style={{ margin: '24px 0' }}
            />
          ) : (
            <>
              {/* Search and Filter Controls */}
              <VenueAssignmentControls>
                <Search
                  placeholder='Search venues by name...'
                  value={venueSearchTerm}
                  onChange={e => setVenueSearchTerm(e.target.value)}
                  allowClear
                  style={{ marginBottom: 12 }}
                />
                <VenueFilterButtons>
                  <Button
                    size='small'
                    type={venueFilterType === 'all' ? 'primary' : 'default'}
                    onClick={() => setVenueFilterType('all')}
                  >
                    All
                  </Button>
                  <Button
                    size='small'
                    type={
                      venueFilterType === 'onThisFile' ? 'primary' : 'default'
                    }
                    onClick={() => setVenueFilterType('onThisFile')}
                  >
                    On this file
                  </Button>
                  <Button
                    size='small'
                    type={
                      venueFilterType === 'onOtherFiles' ? 'primary' : 'default'
                    }
                    onClick={() => setVenueFilterType('onOtherFiles')}
                  >
                    On other files
                  </Button>
                </VenueFilterButtons>
                <VenueActionButtons>
                  <Button
                    size='small'
                    onClick={handleSelectAllFiltered}
                    disabled={isVenueAssignmentSaving}
                  >
                    Select All
                  </Button>
                  <Button
                    size='small'
                    onClick={handleDeselectAllFiltered}
                    disabled={isVenueAssignmentSaving}
                  >
                    Deselect All
                  </Button>
                </VenueActionButtons>
                <VenueCountInfo>
                  Showing {filteredVenues.length} of {allVenues.length} venues
                  {selectedVenuesForAssignment.length > 0 && (
                    <span>
                      {' '}
                      • {selectedVenuesForAssignment.length} selected,{' '}
                      {venuesToMove.length} will be moved
                    </span>
                  )}
                </VenueCountInfo>
              </VenueAssignmentControls>

              <Checkbox.Group
                style={{ width: '100%' }}
                value={selectedVenuesForAssignment}
                onChange={setSelectedVenuesForAssignment}
              >
                <VenueCheckboxList>
                  {filteredVenues.length === 0 ? (
                    <Empty
                      description='No venues match your search or filter'
                      style={{ margin: '24px 0' }}
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  ) : (
                    filteredVenues.map(venue => {
                      const assignment = venueToPriceFileMap.get(venue._id)
                      const isOnCurrentFile =
                        assignment?.priceFileId === selectedPriceFileId
                      const isOnDifferentFile = assignment && !isOnCurrentFile

                      return (
                        <VenueCheckboxItem key={venue._id}>
                          <Checkbox
                            value={venue._id}
                            disabled={isOnCurrentFile}
                          >
                            <VenueName>{venue.title}</VenueName>
                            {isOnCurrentFile && (
                              <Tag color='green' style={{ marginLeft: 8 }}>
                                Currently on this file
                              </Tag>
                            )}
                            {isOnDifferentFile && (
                              <Tag color='geekblue' style={{ marginLeft: 8 }}>
                                Currently on {assignment.priceFileName}
                              </Tag>
                            )}
                          </Checkbox>
                          {isOnDifferentFile &&
                            selectedVenuesForAssignment.includes(venue._id) && (
                              <VenueMoveHint>
                                ⚠️ Will move from {assignment.priceFileName} →{' '}
                                {currentPriceFileName}
                              </VenueMoveHint>
                            )}
                        </VenueCheckboxItem>
                      )
                    })
                  )}
                </VenueCheckboxList>
              </Checkbox.Group>

              {venuesToMove.length > 0 && (
                <VenueAssignmentSummary>
                  <strong>
                    {venuesToMove.length} venue
                    {venuesToMove.length > 1 ? 's' : ''} will be moved to this
                    price file
                  </strong>
                </VenueAssignmentSummary>
              )}
            </>
          )}
        </VenueAssignmentContent>
      </Modal>

      {/* Create New Price File Modal (frontend-only, mocked backend) */}
      <Modal
        title={
          createStep === 1
            ? 'Create a new price file'
            : 'Add products to your new price file'
        }
        visible={isCreatePriceFileModalVisible}
        onCancel={() => {
          setIsCreatePriceFileModalVisible(false)
          resetCreatePriceFileState()
        }}
        footer={
          createStep === 1
            ? [
                <Button
                  key='cancel'
                  onClick={() => {
                    setIsCreatePriceFileModalVisible(false)
                    resetCreatePriceFileState()
                  }}
                >
                  Cancel
                </Button>,
                <Button
                  key='next'
                  type='primary'
                  onClick={handleCreateStep1Next}
                >
                  Continue to upload products
                </Button>,
              ]
            : [
                <Button key='back' onClick={() => setCreateStep(1)}>
                  Back
                </Button>,
                <Button
                  key='cancel'
                  onClick={() => {
                    setIsCreatePriceFileModalVisible(false)
                    resetCreatePriceFileState()
                  }}
                >
                  Cancel
                </Button>,
                <Button
                  key='apply'
                  type='primary'
                  onClick={handleCreateApplyChanges}
                  disabled={
                    !createBulkFile ||
                    createIsMappingIncomplete ||
                    isCreateMappingScanning ||
                    isCreateBulkApplying
                  }
                  loading={isCreateBulkApplying}
                >
                  Create price file &amp; import products
                </Button>,
              ]
        }
        width={760}
      >
        {createStep === 1 && (
          <BulkModalContent>
            <BulkIntroBanner>
              <BulkIntroIconWrapper>
                <InfoCircleOutlined />
              </BulkIntroIconWrapper>
              <BulkIntroBody>
                <BulkIntroContent>
                  <strong>Step 1 · Set up your new price file</strong>
                  <p>
                    You&apos;re creating a brand new price file for{' '}
                    <strong>{getBaseSupplierInfo().title}</strong>. First, tell
                    us the basics. Next, you&apos;ll upload your latest price
                    file so we can add products.
                  </p>
                </BulkIntroContent>
              </BulkIntroBody>
            </BulkIntroBanner>
            <Form layout='vertical'>
              <Form.Item label='Supplier name (title)'>
                <Input value={getBaseSupplierInfo().title} disabled />
              </Form.Item>
              {(() => {
                const base = getBaseSupplierInfo()
                if (base.integrationType) {
                  return (
                    <>
                      <Form.Item label='Integration'>
                        <Input value={base.integrationType} disabled />
                      </Form.Item>
                      <Form.Item>
                        <BulkHelperText>
                          Orders from this price file will use your existing
                          integration with{' '}
                          <strong>{base.integrationType}</strong> and flow
                          straight into your system.
                        </BulkHelperText>
                      </Form.Item>
                    </>
                  )
                }
                return (
                  <Form.Item label='Integration'>
                    <Input value='Not connected yet' disabled />
                    <BulkHelperText>
                      You&apos;re not integrated with a back-office system yet.
                      When you are, this will auto-populate with your
                      integration ID and orders from this price file will flow
                      directly into your system. Get in touch with OMS if
                      you&apos;d like to integrate.
                    </BulkHelperText>
                  </Form.Item>
                )
              })()}
              <Form.Item
                label={
                  <span>
                    Price file name <RequiredPill>Required</RequiredPill>
                  </span>
                }
                required
                validateStatus={
                  createPriceFileErrors.secondTitle ? 'error' : undefined
                }
                help={createPriceFileErrors.secondTitle}
              >
                <Input
                  placeholder='e.g. Original Price File, Hotels – London'
                  value={createPriceFileForm.secondTitle}
                  onChange={e =>
                    setCreatePriceFileForm(prev => ({
                      ...prev,
                      secondTitle: e.target.value,
                    }))
                  }
                />
                <BulkHelperText>
                  What you call this file internally. Examples: &quot;Original
                  Price File&quot;, &quot;Hotels – London&quot;, &quot;£0
                  Onboarding File&quot;.
                </BulkHelperText>
              </Form.Item>
              <Form.Item
                label={
                  <span>
                    Order email address <RequiredPill>Required</RequiredPill>
                  </span>
                }
                required
                validateStatus={
                  createPriceFileErrors.orderEmail ? 'error' : undefined
                }
                help={createPriceFileErrors.orderEmail}
              >
                <Input
                  placeholder='Where orders from this price file will be sent'
                  value={createPriceFileForm.orderEmail}
                  onChange={e =>
                    setCreatePriceFileForm(prev => ({
                      ...prev,
                      orderEmail: e.target.value,
                    }))
                  }
                />
                <BulkHelperText>
                  Orders from venues using this price file will be sent to this
                  inbox.
                </BulkHelperText>
              </Form.Item>
              <Form.Item
                label={
                  <span>
                    Contact number <RequiredPill>Required</RequiredPill>
                  </span>
                }
                required
                validateStatus={
                  createPriceFileErrors.contactNumber ? 'error' : undefined
                }
                help={createPriceFileErrors.contactNumber}
              >
                <Input
                  placeholder='Shown to venues for questions about their orders'
                  value={createPriceFileForm.contactNumber}
                  onChange={e =>
                    setCreatePriceFileForm(prev => ({
                      ...prev,
                      contactNumber: e.target.value,
                    }))
                  }
                />
                <BulkHelperText>
                  Shown to venues for questions about their orders – head office
                  or the sales rep responsible for this price file.
                </BulkHelperText>
              </Form.Item>
              <Form.Item
                label='Minimum order value'
                validateStatus={
                  createPriceFileErrors.minOrderValue ? 'error' : undefined
                }
                help={
                  createPriceFileErrors.minOrderValue ||
                  'Optional – leave blank for no minimum.'
                }
              >
                <Input
                  placeholder='e.g. 100'
                  value={createPriceFileForm.minOrderValue}
                  onChange={e =>
                    setCreatePriceFileForm(prev => ({
                      ...prev,
                      minOrderValue: e.target.value,
                    }))
                  }
                />
              </Form.Item>
              <Form.Item
                label={
                  <span>
                    Delivery days <RequiredPill>Required</RequiredPill>
                  </span>
                }
                required
                validateStatus={
                  createPriceFileErrors.deliveryDays ? 'error' : undefined
                }
                help={
                  createPriceFileErrors.deliveryDays ||
                  'At least one delivery day is required.'
                }
              >
                <DeliveryDaysSelect
                  deliveryDays={createPriceFileForm.deliveryDays}
                  onChange={value =>
                    setCreatePriceFileForm(prev => ({
                      ...prev,
                      deliveryDays: value,
                    }))
                  }
                  isDisabled={false}
                />
              </Form.Item>
              <Form.Item label='Cut-off time (optional)'>
                <Input
                  placeholder='e.g. 16:00'
                  value={createPriceFileForm.cutOffTime}
                  onChange={e =>
                    setCreatePriceFileForm(prev => ({
                      ...prev,
                      cutOffTime: e.target.value,
                    }))
                  }
                />
                <BulkHelperText>
                  Orders placed after this time will roll to the next available
                  delivery day.
                </BulkHelperText>
              </Form.Item>
            </Form>
          </BulkModalContent>
        )}

        {createStep === 2 && (
          <BulkModalContent>
            <BulkSection>
              <BulkSectionTitle>
                Step 1 · Upload your latest price file
              </BulkSectionTitle>
              <Upload.Dragger
                {...createUploadProps}
                accept='.csv,.tsv,.xls,.xlsx'
              >
                <p className='ant-upload-drag-icon'>
                  <InboxOutlined />
                </p>
                <p className='ant-upload-text'>
                  Drag &amp; drop your file, or click to browse
                </p>
                <UploadTip>
                  Upload your latest CSV or Excel price file. We&apos;ll scan
                  the headers and help you map them to OMS fields.
                </UploadTip>
              </Upload.Dragger>
              {isCreateMappingScanning && (
                <div style={{ marginTop: 12 }}>
                  <Spin tip='Scanning headers...' />
                </div>
              )}
            </BulkSection>

            <BulkSection>
              <BulkSectionTitle>Step 2 · Map your columns</BulkSectionTitle>
              {!createBulkFile && (
                <BulkHelperText>
                  Upload a file above to start mapping your headers to OMS
                  fields.
                </BulkHelperText>
              )}
              {!!createBulkFile && (
                <MappingGrid>
                  {bulkMappingFields.map(field => (
                    <MappingRow key={field.key}>
                      <FieldLabel>
                        {field.label}
                        {field.required && (
                          <RequiredPill>Required</RequiredPill>
                        )}
                      </FieldLabel>
                      <Select
                        placeholder='Select a column'
                        value={createBulkFieldMapping[field.key] || undefined}
                        onChange={value =>
                          handleCreateMappingChange(field.key, value)
                        }
                        allowClear
                        showSearch
                      >
                        {createBulkFileHeaders.map(header => (
                          <Select.Option
                            key={`${field.key}-${header}`}
                            value={header}
                          >
                            {header}
                          </Select.Option>
                        ))}
                      </Select>
                      <FieldHelper>{field.helper}</FieldHelper>
                    </MappingRow>
                  ))}
                </MappingGrid>
              )}
              {createIsMappingIncomplete && !!createBulkFile && (
                <Alert
                  type='warning'
                  showIcon
                  message='Please map all required fields before continuing.'
                  style={{ marginTop: 16 }}
                />
              )}
            </BulkSection>

            <BulkSection>
              <BulkSectionTitle>Step 3 · Review &amp; apply</BulkSectionTitle>
              <BulkHelperText>
                When you apply changes, we&apos;ll create or update products in
                this new price file. Existing products are never removed from a
                brand new file.
              </BulkHelperText>
            </BulkSection>
          </BulkModalContent>
        )}
      </Modal>
    </Wrapper>
  )
}

const EmptyState = () => {
  return (
    <EmptyStateContainer>
      <Empty
        image={<FileTextOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />}
        description={
          <EmptyStateDescription>
            <EmptyStateTitle>
              Select a price file to get started
            </EmptyStateTitle>
            <EmptyStateText>
              Choose a price file from the dropdown above to view and manage its
              products.
            </EmptyStateText>
          </EmptyStateDescription>
        }
      />
    </EmptyStateContainer>
  )
}

const LoadingState = styled.div`
  text-align: center;
  padding: 32px;
`

const Wrapper = styled(FadeIn)`
  width: 100%;
`

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
`

const CreateNewPriceFileButton = styled(Button)`
  height: 40px;
  font-weight: 500;
`

const PriceFileSelectorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const Label = styled.label`
  font-weight: 600;
  font-size: 14px;
  color: ${({ theme }) => theme.colours.text || '#262626'};
`

const EmptyStateContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  padding: 40px 20px;
`

const EmptyStateDescription = styled.div`
  text-align: center;
`

const EmptyStateTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colours.text || '#262626'};
  margin-bottom: 8px;
  margin-top: 0;
`

const EmptyStateText = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colours.muted || '#8c8c8c'};
  margin: 0;
`

const MetadataCard = styled(Card)`
  .ant-card-body {
    padding: 20px;
  }
`

const MetadataHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  color: ${({ theme }) => theme.colours.text || '#262626'};
`

const MetadataTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin: 0;
`

const MetadataContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const MetadataItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const MetadataLabel = styled.span`
  font-weight: 500;
  color: ${({ theme }) => theme.colours.text || '#262626'};
  min-width: 140px;
  display: flex;
  align-items: center;
`

const MetadataValue = styled.span`
  color: ${({ theme }) => theme.colours.text || '#262626'};
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`

const VenueToggle = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colours.primary || '#1890ff'};
  cursor: pointer;
  padding: 0;
  margin-left: 8px;
  font-size: 14px;
  text-decoration: underline;

  &:hover {
    opacity: 0.8;
  }
`

const VenueList = styled.div`
  margin-top: 8px;
  padding: 12px;
  background: ${({ theme }) => theme.colours.bg || '#fafafa'};
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 200px;
  overflow-y: auto;
`

const VenueItem = styled.div`
  padding: 6px 12px;
  background: white;
  border-radius: 4px;
  font-size: 14px;
  color: ${({ theme }) => theme.colours.text || '#262626'};
`

const SearchCard = styled(Card)`
  .ant-card-body {
    padding: 16px;
  }
`

const ImagePlaceholder = styled.div`
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colours.bg || '#fafafa'};
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colours.border || '#d9d9d9'};
`

const ActionButtons = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`

const InfoBanner = styled(Alert)`
  border-radius: 6px;
`

const IntegrationAdvertCard = styled(Card)`
  background: ${({ theme }) => theme.colours.bg || '#fafafa'};
  border: 1px solid ${({ theme }) => theme.colours.border || '#d9d9d9'};
  .ant-card-body {
    padding: 12px 16px;
  }
`

const IntegrationAdvertContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  position: relative;
`

const IntegrationAdvertIcon = styled.div`
  flex-shrink: 0;
`

const IntegrationAdvertText = styled.div`
  flex: 1;
`

const IntegrationAdvertTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: ${({ theme }) => theme.colours.text || '#262626'};
  margin-bottom: 2px;
`

const IntegrationAdvertDescription = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colours.text || '#595959'};
  margin-bottom: 6px;
`

const IntegrationAdvertLink = styled.a`
  color: ${({ theme }) => theme.colours.primary || '#1890ff'};
  font-weight: 500;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`

const CloseButton = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colours.text || '#8c8c8c'};
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background: rgba(0, 0, 0, 0.06);
    color: ${({ theme }) => theme.colours.text || '#262626'};
  }
`

const EducationalCard = styled(Card)`
  .ant-card-body {
    padding: 0;
  }
  .ant-collapse {
    border: none;
    background: transparent;
  }
  .ant-collapse-item {
    border: none;
  }
  .ant-collapse-header {
    padding: 16px 20px !important;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    border-radius: 4px;

    &:hover {
      background: ${({ theme }) => theme.colours.bg || '#fafafa'};
    }
  }
  .ant-collapse-content-box {
    padding: 20px !important;
  }
  .ant-collapse-arrow {
    right: 20px !important;
    left: auto !important;
    padding-right: 0 !important;
  }
`

const EducationalHeader = styled.div`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colours.text || '#262626'};
  gap: 12px;

  .anticon {
    flex-shrink: 0;
  }
`

const EducationalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const EducationalSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const EducationalSectionTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colours.text || '#262626'};
  margin: 0;
`

const EducationalText = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colours.text || '#595959'};
  line-height: 1.6;
  margin: 0;
`

const EducationalList = styled.ul`
  font-size: 14px;
  color: ${({ theme }) => theme.colours.text || '#595959'};
  line-height: 1.8;
  margin: 0;
  padding-left: 20px;

  li {
    margin-bottom: 8px;
  }
`

const EducationalSteps = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const EducationalStep = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colours.text || '#595959'};
  line-height: 1.6;
  padding: 12px;
  background: ${({ theme }) => theme.colours.bg || '#fafafa'};
  border-radius: 4px;
  border-left: 3px solid ${({ theme }) => theme.colours.primary || '#1890ff'};
`

const MetadataActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
  flex-wrap: wrap;
`

const VenueAssignmentContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const VenueAssignmentInfo = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colours.text || '#595959'};
  padding: 12px;
  background: ${({ theme }) => theme.colours.bg || '#fafafa'};
  border-radius: 4px;
  border-left: 3px solid ${({ theme }) => theme.colours.primary || '#1890ff'};
`

const VenueCheckboxList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 400px;
  overflow-y: auto;
  padding: 8px;
  border: 1px solid ${({ theme }) => theme.colours.border || '#d9d9d9'};
  border-radius: 4px;
  background: ${({ theme }) => theme.colours.bg || '#fafafa'};
`

const VenueCheckboxItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${({ theme }) => theme.colours.bg || '#f5f5f5'};
  }
`

const VenueName = styled.span`
  font-weight: 500;
  color: ${({ theme }) => theme.colours.text || '#262626'};
`

const VenueMoveHint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colours.warning || '#faad14'};
  margin-left: 24px;
  margin-top: 4px;
  font-style: italic;
`

const VenueAssignmentSummary = styled.div`
  padding: 12px;
  background: ${({ theme }) => theme.colours.infoBg || '#e6f7ff'};
  border: 1px solid ${({ theme }) => theme.colours.infoBorder || '#91d5ff'};
  border-radius: 4px;
  color: ${({ theme }) => theme.colours.infoText || '#0958d9'};
  text-align: center;
`

const VenueAssignmentControls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
`

const VenueFilterButtons = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`

const VenueActionButtons = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`

const VenueCountInfo = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colours.text || '#8c8c8c'};
  margin-top: 4px;

  span {
    color: ${({ theme }) => theme.colours.primary || '#1890ff'};
    font-weight: 500;
  }
`

const BulkModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const BulkIntroBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: #e6f4ff;
  border: 1px solid #bae0ff;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 8px;
`

const BulkIntroIconWrapper = styled.div`
  font-size: 20px;
  color: ${({ theme }) => theme.colours.primary || '#1890ff'};
`

const BulkIntroBody = styled.div`
  display: flex;
  flex: 1;
  gap: 16px;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
`

const BulkIntroContent = styled.div`
  flex: 1;

  p {
    margin: 6px 0;
  }
`

const BulkSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const BulkSectionTitle = styled.h4`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
`

const BulkHelperText = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colours.muted || '#8c8c8c'};
  font-size: 13px;
`

const UploadTip = styled.p`
  margin-top: 8px;
  color: ${({ theme }) => theme.colours.muted || '#8c8c8c'};
`

const MappingGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const MappingRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const FieldLabel = styled.div`
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
`

const FieldHelper = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colours.muted || '#8c8c8c'};
`

const RequiredPill = styled.span`
  background: ${({ theme }) => theme.colours.primary || '#1890ff'};
  color: white;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 999px;
  text-transform: uppercase;
`

export default SupplierProductFiles
